"use client";

// LOT 3.6 - Reps : player de lecon, game-feel facon Duolingo/Mimo.
// Theme CLAIR par defaut + dark: conserve. BeBot affiche UNIQUEMENT a la reponse
// (juste/faux), sans levitation. Explication via bouton + modale (style QuizView).
// Reprise des erreurs (file d'attente). Modale de confirmation a la sortie.

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslations, useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import { useSound } from "@/contexts/SoundContext";
import { useSession } from "next-auth/react";
import SoundSettingsPopover from "@/components/ui/SoundSettings/SoundSettingsPopover";
import RepsLearningSettings from "@/components/Reps/RepsLearningSettings";
import { TOKEN_DARK, tokenizeLines } from "@/lib/reps/highlight";
import {
    DEFAULT_LEARNING_PREFS,
    fetchLearningPrefs,
    getLearningPrefs,
    pushLearningPrefs,
    saveLearningPrefs,
} from "@/lib/reps/learningPrefs";
import ReportModal from "@/components/ui/ReportModal/ReportModal";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import {
    AnimatePresence,
    motion,
    useAnimation,
    useReducedMotion,
} from "motion/react";
import ConfettiExplosion from "react-confetti-explosion";
import { Link, getPathname, useRouter } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BebotMascot from "@/components/ui/BebotMascot/BebotMascot";
import RepsExercise, {
    PreviewToggleContext,
    isWideStep,
    renderMarkdownInline,
} from "./RepsExercise";
import { gradeExercise } from "@/lib/reps/grade";
import { warmReps } from "@/lib/reps/pyodideRunner";
import { hasRepsAssistant } from "@/lib/reps/access";
import {
    hasDoneRepsToday,
    markRepsActivityToday,
} from "@/lib/reps/dailyActivity";
import RepsOutOfKeys from "@/components/Reps/RepsOutOfKeys";
import RepsKeyUnlock from "@/components/Reps/RepsKeyUnlock";
import RepsContinueButton from "@/components/Reps/RepsContinueButton";
import RepsQuestsCelebration from "@/components/Reps/RepsQuestsCelebration";
import RepsBadgeCelebration from "@/components/Reps/RepsBadgeCelebration";
import { buildQuestsUpdate } from "@/lib/quests/questProgress";
import RepsSignupGate, {
    RepsSignupButtons,
} from "@/components/Reps/RepsSignupGate";
import {
    RepsAssistantDock,
    RepsAssistantLauncher,
    RepsAssistantPanel,
    RepsAssistantHandle,
} from "@/components/Reps/RepsAssistant";
import {
    LEGENDARY_COLORS,
    LEGENDARY_FACETS,
    LEGENDARY_LIVES,
    LEGENDARY_REVIEW_XP,
    LEGENDARY_XP,
    isLegendaryDone,
    markLegendaryDone,
} from "@/lib/reps/legendary";

const INLINE_CODE =
    "mx-0.5 rounded px-1.5 py-0.5 font-geistMono text-[0.88em] text-primary bg-primary/10";

// Types dont le VERDICT peint déjà la bonne réponse (l'option ou la ligne
// verte, même non choisie) : l'apprenant la voit sans qu'on la lui montre.
const TYPES_REPONSE_PEINTE = [
    "mcq",
    "multi_select",
    "predict_output",
    "find_error",
    "tap_target",
];

// Combo : a partir de 2 bonnes reponses d'affilee, l'eclair de serie se colore.
// La BARRE, elle, ne change JAMAIS de couleur (comme chez Duolingo et Brilliant :
// la teinte du remplissage encode le mode de session, pas la performance).
const COMBO_CHARGE = 2;

// Serie CHAUDE : l'eclair passe en pastille pleine et la casse devient visible
// (secousse). La teinte, elle, a fini de monter a 4 : au-dela c'est la FORME qui
// change, sinon on retombe dans le defile de couleurs qu'on vient de retirer.
const COMBO_HOT = 5;

// Gradation de l'eclair : gris tant qu'il n'y a pas de serie (0 et 1), puis la
// couleur monte jusqu'a l'orange d'action. Un cran d'alpha en moins en sombre :
// sur fond fonce, un orange pale ressort plus qu'un orange sature.
function boltTone(combo) {
    if (combo >= 4) return "text-orange-500 dark:text-orange-400";
    if (combo === 3) return "text-orange-400 dark:text-orange-400/85";
    if (combo === COMBO_CHARGE) return "text-orange-300 dark:text-orange-300/70";
    return "text-gray-300 dark:text-white/20";
}

// renderMarkdownInline vient de RepsExercise (backticks, balises et mots-clés
// de code stylés + **gras** + *italique*, sans liens) : les consignes portent
// la même voix que les concepts (gras sur LE mot qui compte, cf METHOD.md).

// Compteur qui monte (requestAnimationFrame, jamais de MotionValue en enfant JSX).
function CountUp({ to, duration = 800, suffix = "" }) {
    const [v, setV] = useState(0);
    const reduce = useReducedMotion();
    useEffect(() => {
        if (reduce) {
            setV(to);
            return;
        }
        let raf;
        const start = performance.now();
        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setV(to * eased);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [to, duration, reduce]);
    return (
        <>
            {Math.round(v)}
            {suffix}
        </>
    );
}

// Messages BeBot du palier de mi-parcours (voix BeBot, pioches au hasard, poses variees).
const MID_CHEERS = [
    { pose: "happy-right", textKey: "reps_mid_cheer_01" },
    { pose: "malicious-right", textKey: "reps_mid_cheer_02" },
    { pose: "happy-right", textKey: "reps_mid_cheer_03" },
    { pose: "surprised-right", textKey: "reps_mid_cheer_04" },
    { pose: "malicious-right", textKey: "reps_mid_cheer_05" },
    { pose: "happy-right", textKey: "reps_mid_cheer_06" },
    { pose: "surprised-right", textKey: "reps_mid_cheer_07" },
    { pose: "malicious-right", textKey: "reps_mid_cheer_08" },
    { pose: "happy-right", textKey: "reps_mid_cheer_09" },
    { pose: "malicious-right", textKey: "reps_mid_cheer_10" },
    { pose: "happy-right", textKey: "reps_mid_cheer_11" },
    { pose: "malicious-right", textKey: "reps_mid_cheer_12" },
    { pose: "surprised-right", textKey: "reps_mid_cheer_13" },
    { pose: "happy-right", textKey: "reps_mid_cheer_14" },
    { pose: "malicious-right", textKey: "reps_mid_cheer_15" },
    { pose: "happy-right", textKey: "reps_mid_cheer_16" },
];

// Messages BeBot de l'entree en revision des erreurs (meme style que le mi-parcours).
const REVIEW_CHEERS = [
    { pose: "happy-right", textKey: "reps_review_cheer_01" },
    { pose: "malicious-right", textKey: "reps_review_cheer_02" },
    { pose: "happy-right", textKey: "reps_review_cheer_03" },
];

// Messages BeBot d'un COMBO (serie de bonnes reponses d'affilee) : le moment le
// plus gratifiant, on sort BeBot plein cadre. Les textes affichent le nombre.
// 30 variantes piochees au hasard -> on ne retombe quasi jamais sur le meme.
const COMBO_CHEERS = Array.from({ length: 30 }, (_, i) => {
    const poses = ["malicious-right", "surprised-right", "happy-right"];
    const n = String(i + 1).padStart(2, "0");
    return { pose: poses[i % poses.length], textKey: `reps_combo_cheer_${n}` };
});

// Paliers de serie qui declenchent l'interstitiel "en feu".
const COMBO_CHEER_AT = [4, 8];

// Quotas QUOTIDIENS des interstitiels BeBot "riches" (BeBot plein cadre), pour
// celebrer sans jamais lasser quand on enchaine les lecons :
//  - mi-parcours : au plus 2 par jour (c'est le rituel qui se repetait) ;
//  - combo de 4 : frequent -> plafonne a 5 par jour ;
//  - combo de 8 : rare et impressionnant -> illimite (toujours plaisant a voir).
const MID_CAP_PER_DAY = 2;
const COMBO_CAP_PER_DAY = { 4: 5, 8: Infinity };
const RICH_BUDGET_KEY = "reps_rich_interstitials";
// Anti-clic fantôme : quand le groupe d'actions bascule (le verdict tombe,
// « Vérifier » devient « Continuer »), un clic déjà parti activerait une action
// que personne n'a voulue. On ne jette QUE ces clics-là — reconnaissables à
// trois signes : l'appui a commencé avant la bascule, c'est le 2e coup d'un
// double-clic, ou c'est la touche Entrée maintenue enfoncée.
// Cette fenêtre aveugle ne sert plus que de filet pour le double-tap tactile,
// qui n'a aucun de ces signes. Elle valait 350 ms : elle avalait alors des
// clics parfaitement délibérés, d'où l'impression d'un clic qui ne part pas.
// Elle ne s'applique QUE sur un échec — ce qu'elle protège, c'est la
// correction. Après une bonne réponse, le bouton répond au quart de tour.
const GHOST_CLICK_MS = 140;
const GHOST_TAP_MS = 320;

function comboCap(tier) {
    const cap = COMBO_CAP_PER_DAY[tier];
    return cap === undefined ? Infinity : cap;
}

function richBudgetToday() {
    // Cle locale AAAA-M-J : le fuseau du navigateur = celui de l'apprenant.
    const d = new Date();
    const day = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    const empty = { day, mid: 0, combo: {} };
    if (typeof window === "undefined") return empty;
    try {
        const raw = window.localStorage.getItem(RICH_BUDGET_KEY);
        const p = raw ? JSON.parse(raw) : null;
        if (p && p.day === day) {
            return { day, mid: p.mid || 0, combo: p.combo || {} };
        }
    } catch (e) {
        // stockage indisponible (nav prive, quota) : on repart a zero.
    }
    return empty;
}

function canShowMid() {
    return richBudgetToday().mid < MID_CAP_PER_DAY;
}

// tier = 4 | 8. Le palier de combo a-t-il encore du quota aujourd'hui ?
function canShowCombo(tier) {
    if (comboCap(tier) === Infinity) return true;
    return (richBudgetToday().combo[tier] || 0) < comboCap(tier);
}

function bumpMidBudget() {
    if (typeof window === "undefined") return;
    const b = richBudgetToday();
    try {
        window.localStorage.setItem(
            RICH_BUDGET_KEY,
            JSON.stringify({ day: b.day, mid: b.mid + 1, combo: b.combo }),
        );
    } catch (e) {
        // stockage indisponible : on ne bloque pas la lecon pour autant.
    }
}

function bumpComboBudget(tier) {
    // Palier illimite : rien a compter, on n'ecrit meme pas.
    if (typeof window === "undefined" || comboCap(tier) === Infinity) return;
    const b = richBudgetToday();
    const combo = { ...b.combo, [tier]: (b.combo[tier] || 0) + 1 };
    try {
        window.localStorage.setItem(
            RICH_BUDGET_KEY,
            JSON.stringify({ day: b.day, mid: b.mid, combo }),
        );
    } catch (e) {
        // stockage indisponible : on ne bloque pas la lecon pour autant.
    }
}

function pickCheer(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

// Corps partage des interstitiels BeBot : le personnage surgit du bas + une bulle (facon Duo).
function CheerBody({ pose, message }) {
    return (
        <div className="flex w-full max-w-md items-end gap-3">
            <motion.img
                src={`/images/bebot/coach/${pose.replace("-right", "")}.webp`}
                alt="BeBot"
                initial={{ y: 64, opacity: 0, rotate: -6 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className="h-28 w-28 shrink-0 object-contain"
            />
            <motion.div
                initial={{ scale: 0.7, opacity: 0, y: 10 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{
                    type: "spring",
                    stiffness: 320,
                    damping: 20,
                    delay: 0.12,
                }}
                className="relative mb-3 rounded-2xl rounded-bl-md border border-gray-200 bg-white px-4 py-3 text-left text-[15px] font-medium leading-snug text-gray-800 shadow-sm dark:border-white/10 dark:bg-[#253340] dark:text-grayDark-100"
            >
                {message}
            </motion.div>
        </div>
    );
}

const WEEK_DAYS = ["L", "M", "M", "J", "V", "S", "D"];

// Petits messages de bonne reponse (varient a chaque fois -> plus gratifiant).
// Ils servent aussi de phrases VOCALES : BeBot les dit parfois a voix haute
// (ElevenLabs, cache R2 cote serveur -> chaque phrase n'est synthetisee
// qu'une fois, tous apprenants confondus).
const CORRECT_CHEERS = [
    "reps_correct_cheer_01",
    "reps_correct_cheer_02",
    "reps_correct_cheer_03",
    "reps_correct_cheer_04",
    "reps_correct_cheer_05",
    "reps_correct_cheer_06",
    "reps_correct_cheer_07",
    "reps_correct_cheer_08",
    "reps_correct_cheer_09",
    "reps_correct_cheer_10",
    "reps_correct_cheer_11",
    "reps_correct_cheer_12",
];

// Réaction BeBot = bulle + voix ENSEMBLE (jamais l'une sans l'autre ; la
// voix ne manque que si l'utilisateur l'a désactivée, palier sans voix
// BeBot, ou échec réseau — la bulle reste). Bonne réponse : récompense
// VARIABLE — toujours à la 1re de la leçon, puis ~1 fois sur 3 et jamais
// deux fois de suite (le jingle + le cadre vert assurent le feedback
// constant ; la rareté fait la surprise). Mauvaise réponse : la bulle est
// fonctionnelle (rattrapage + contestation), elle s'affiche TOUJOURS et la
// voix l'accompagne à chaque fois.
const OK_REACT_P = 0.4; // avec l'anti-répétition => ~1 réaction sur 3

// Grâce maximale accordée au pipeline de fin quand l'apprenant appuie sur
// Continuer avant que le serveur ait répondu : au-delà, on sort sans écran de
// quêtes (la progression, elle, reste créditée).
const QUESTS_GRACE_MS = 1200;

// Attente maximale de l'écriture du temps d'apprentissage avant d'envoyer le
// submit : au-delà, on soumet quand même (la barre d'objectif sera juste en
// retard d'une leçon — jamais l'inverse, une leçon non créditée).
const TIME_FLUSH_GRACE_MS = 1500;

// Quêtes du jour et badge du mois — lus deux fois autour d'une leçon (avant /
// après) pour raconter la progression. Toujours silencieux en cas d'échec :
// ces écrans sont un bonus, jamais une condition de la fin de leçon. On
// n'envoie PAS de fuseau : la route retombe sur celui du compte, seule source
// qui ne dépende pas du navigateur.
async function fetchQuests() {
    try {
        const r = await fetch("/api/quests/daily", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
        });
        return r.ok ? await r.json() : null;
    } catch (e) {
        return null;
    }
}

async function fetchBadgeSummary(locale) {
    try {
        const r = await fetch("/api/badges/summary", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locale: locale === "en" ? "en" : "fr" }),
        });
        return r.ok ? await r.json() : null;
    } catch (e) {
        return null;
    }
}

// Apparition en cascade des lignes du récapitulatif de fin de leçon : fondu +
// 8 px, jamais de rebond (règle d'animation maison).
const STAT_ROW_VARIANTS = {
    hidden: { opacity: 0, y: 8 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.28, ease: "easeOut" },
    },
};

// Ecran de celebration de serie (style Duolingo) : flamme + BeBot, gros nombre,
// calendrier de la semaine. Plein ecran, apres l'ecran de stats de fin de lecon.
function StreakCelebration({ streak, onContinue, leaving }) {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const todayIdx = (new Date().getDay() + 6) % 7; // lundi = 0
    const fromIdx = Math.max(0, todayIdx - streak + 1);
    // La coche d'AUJOURD'HUI ne s'affiche pas d'emblée : la pastille arrive
    // grise comme les jours à venir, puis le tampon orange SE POSE (petit
    // moment de récompense, après l'arrivée du chiffre). Mouvement réduit :
    // coche immédiate, sans mise en scène.
    const [stamped, setStamped] = useState(false);
    useEffect(() => {
        if (reduce) {
            setStamped(true);
            return undefined;
        }
        const id = setTimeout(() => {
            setStamped(true);
            try {
                if (navigator.vibrate) navigator.vibrate(35);
            } catch (e) {
                /* ignore */
            }
        }, 950);
        return () => clearTimeout(id);
    }, [reduce]);
    return (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center overflow-hidden bg-[#f5f5fa] px-6 text-center text-gray-900 dark:bg-[#192734] dark:text-white">
            <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 240, damping: 22 }}
                className="flex flex-col items-center"
            >
                {/* Vraie bulle avec une pointe vers BeBot : c'est lui qui parle. */}
                <div className="relative mb-4 max-w-xs rounded-3xl border border-gray-200 bg-white px-6 py-3.5 text-[15px] font-medium text-gray-700 shadow-sm dark:border-white/10 dark:bg-[#253340] dark:text-grayDark-100">
                    {t("reps_streak_bubble")}
                    <span className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-x-[9px] border-t-[9px] border-x-transparent border-t-white dark:border-t-[#253340]" />
                </div>

                {/* BeBot anime (Lottie), comme l'ecran de fin. */}
                <BebotMascot width="190px" />

                {/* Chiffre de serie : apparition au ressort (framer-motion). */}
                <motion.div
                    initial={{ scale: 0.2, opacity: 0, y: 8 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{
                        type: "spring",
                        stiffness: 320,
                        damping: 13,
                        delay: 0.25,
                    }}
                    className="text-[5rem] font-extrabold leading-none text-orange-500"
                >
                    <CountUp to={streak} />
                </motion.div>
                <div className="mt-1 text-base font-bold uppercase tracking-wide text-orange-500">
                    {t("reps_streak_label", { count: streak })}
                </div>

                <div className="mt-7 flex gap-2.5 rounded-2xl border border-orange-100 bg-orange-50/70 px-4 py-3 dark:border-orange-500/20 dark:bg-orange-500/10">
                    {WEEK_DAYS.map((d, i) => {
                        const on = i >= fromIdx && i <= todayIdx;
                        return (
                            <div key={i} className="flex flex-col items-center gap-1.5">
                                <span
                                    className={`text-xs font-bold ${
                                        i === todayIdx
                                            ? "text-orange-500"
                                            : "text-gray-400"
                                    }`}
                                >
                                    {d}
                                </span>
                                {on && i === todayIdx ? (
                                    // Le jour J : pastille grise -> le tampon
                                    // orange se pose au ressort, avec une onde
                                    // discrète à l'impact.
                                    <span className="relative grid h-[26px] w-[26px] place-items-center">
                                        {!stamped && (
                                            <span className="h-[22px] w-[22px] rounded-full bg-gray-200 dark:bg-white/10" />
                                        )}
                                        {stamped && (
                                            <>
                                                <motion.span
                                                    initial={
                                                        reduce
                                                            ? false
                                                            : {
                                                                  scale: 0.6,
                                                                  opacity: 0.7,
                                                              }
                                                    }
                                                    animate={{
                                                        scale: 1.9,
                                                        opacity: 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.5,
                                                        ease: "easeOut",
                                                    }}
                                                    className="absolute inset-0 rounded-full border-2 border-orange-400"
                                                />
                                                <motion.span
                                                    initial={
                                                        reduce
                                                            ? false
                                                            : {
                                                                  scale: 1.7,
                                                                  opacity: 0,
                                                              }
                                                    }
                                                    animate={{
                                                        scale: 1,
                                                        opacity: 1,
                                                    }}
                                                    transition={{
                                                        type: "spring",
                                                        stiffness: 520,
                                                        damping: 16,
                                                    }}
                                                    className="grid place-items-center"
                                                >
                                                    <SolarIcon
                                                        name="check-circle-bold"
                                                        className="text-[26px] text-orange-500"
                                                    />
                                                </motion.span>
                                            </>
                                        )}
                                    </span>
                                ) : on ? (
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="text-[26px] text-orange-500"
                                    />
                                ) : (
                                    <span className="h-[22px] w-[22px] rounded-full bg-gray-200 dark:bg-white/10" />
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Le bouton enchaîne sur la suite de la séquence (quêtes,
                    badge) plutôt que de sortir : c'est la séquence qui décide
                    quand ramener au parcours. */}
                <RepsContinueButton
                    onClick={onContinue}
                    loading={leaving}
                    wide={false}
                    className="mt-8"
                >
                    {t("reps_continue")}
                </RepsContinueButton>
            </motion.div>
        </div>
    );
}

export default function RepsLessonPlayer({ lesson }) {
    const t = useTranslations();
    // Code canonique de la leçon (identique dans toutes les langues) : il part
    // dans tous les payloads serveur. lesson.slug est le slug localisé,
    // réservé aux URLs.
    const lessonKey = lesson.code || lesson.slug;
    // Le code ci-dessus dit une POSITION dans le chemin : il désignerait une
    // autre leçon si le contenu était renuméroté entre ce chargement et
    // l'écriture. On rejoue donc l'identité servie par le back à chaque appel,
    // et c'est elle qu'il regarde en premier. Absente (contenu pas encore
    // réimporté) : il retombe sur le code, comme avant.
    const lessonContentKey =
        typeof lesson.contentKey === "string" && lesson.contentKey
            ? lesson.contentKey
            : null;
    // Locale du contenu SERVI par le back : rejouée telle quelle au submit pour
    // que la correction se fasse contre les solutions réellement affichées,
    // même si l'utilisateur change la langue du site en cours de leçon.
    const contentLocale = lesson.contentLocale || "fr";
    const steps = lesson.steps || lesson.exercises || [];
    const total = steps.length;
    const practiceTotal = steps.filter((s) => s.type !== "concept").length;
    // PRÉCHAUFFAGE DE PYTHON dès l'ouverture d'une leçon qui en contient.
    // Pyodide pèse une dizaine de mégaoctets et vient d'un CDN : sans ça, le
    // premier démarrage tombait au montage de la première console, c'est-à-dire
    // souvent À L'INSTANT du bon verdict — le seul moment de la leçon où
    // l'apprenant n'a aucune raison d'attendre. Ici il se charge pendant qu'il
    // lit le premier écran.
    // Booléen dérivé sans useMemo : `steps` est une expression logique
    // (lesson.steps || lesson.exercises || []), donc une référence neuve à
    // chaque rendu — un useMemo dessus se recalculerait de toute façon, et la
    // dépendance de l'effet doit être la VALEUR, pas le tableau.
    const utilisePython = steps.some(
        (s) =>
            s &&
            (s.type === "python_code" || (s.data && (s.data.run || s.data.reveal)))
    );
    // Une leçon JavaScript n'a rien à préchauffer, et surtout pas Pyodide.
    const leconJs =
        lesson?.language === "javascript" || /^js-/.test(lessonKey || "");
    useEffect(() => {
        if (utilisePython && !leconJs) warmReps();
    }, [utilisePython, leconJs]);
    const reduce = useReducedMotion();
    // Secousse de l'eclair quand une serie chaude se casse (pilotee, pas de key
    // qui remonte : le compteur doit rester en place pendant l'animation).
    const boltControls = useAnimation();
    // Session : vrai streak (barre du haut) + rafraîchissement après la fin de leçon.
    const { data: sessionData, update: updateSession, status } = useSession();
    const locale = useLocale();
    const router = useRouter();
    // MODE REVUE (admin uniquement) : jouer la leçon comme un élève et noter
    // chaque étape d'un V (validée) ou d'un X (invalidée + motif). Le verdict
    // part structuré vers /api/reps/review — journal de métrique côté back,
    // et ticket « review » dans l'onglet Tickets quand l'étape est invalidée.
    // UI en français en dur : outillage d'administration, jamais vu par un
    // apprenant.
    const isAdminReviewer = (sessionData?.user?.roles || []).some(
        (r) => r === "ROLE_ADMIN" || r === "ROLE_SUPER_ADMIN"
    );
    const [reviewMode, setReviewMode] = useState(false);
    // Verdicts déjà posés sur cette session de jeu, par index d'étape :
    // "ok" | "ko". Sert à colorer les boutons et à éviter les doublons
    // involontaires (re-cliquer remplace, côté back chaque ligne est datée).
    const [reviewVerdicts, setReviewVerdicts] = useState({});
    const [showReviewKo, setShowReviewKo] = useState(false);
    const [reviewMotif, setReviewMotif] = useState(null);
    const [reviewNote, setReviewNote] = useState("");
    const [reviewSending, setReviewSending] = useState(false);
    // Navigation libre : null = jeu normal, un nombre = index du step
    // feuilleté (case « Feuilleter » de la pastille de revue, flèches ← →).
    const [reviewBrowse, setReviewBrowse] = useState(null);
    // En feuilletage : afficher la bonne réponse du step courant (panneau
    // ambré au-dessus des flèches, case « Solution » de la pastille).
    const [reviewShowAnswer, setReviewShowAnswer] = useState(false);
    // Le mode survit à la navigation entre leçons (localStorage) : une session
    // de revue enchaîne les leçons d'une unité.
    useEffect(() => {
        if (!isAdminReviewer || typeof window === "undefined") return;
        try {
            if (window.localStorage.getItem("reps_review_mode") === "1")
                setReviewMode(true);
        } catch (e) {
            // stockage indisponible : le toggle reste manuel
        }
    }, [isAdminReviewer]);
    function toggleReviewMode() {
        setReviewMode((on) => {
            const next = !on;
            try {
                window.localStorage.setItem("reps_review_mode", next ? "1" : "0");
            } catch (e) {
                // ignore
            }
            // Quitter le mode revue quitte aussi la navigation libre : un
            // apprenant ne doit jamais se retrouver sur un step feuilleté.
            if (!next) setReviewBrowse(null);
            return next;
        });
    }
    // Pont vers le clavier global (V/X) : le gestionnaire vit dans un effet
    // aux dépendances fermées, il lit l'état du moment via cette ref (même
    // patron que actionRef).
    const reviewRef = useRef({ active: false, ok: () => {}, ko: () => {} });
    // À l'ouverture d'une leçon en mode revue, recharge les verdicts déjà
    // posés (V, X, implicites) : rouvrir une leçon relue ne repart jamais de
    // zéro, et la validation implicite de fin de leçon ne re-valide pas ce
    // qui l'est déjà. Les verdicts posés LOCALEMENT gagnent sur le serveur.
    useEffect(() => {
        if (!isAdminReviewer || !reviewMode || isAnon) return;
        let annule = false;
        (async () => {
            try {
                const res = await fetch("/api/reps/review-state", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        lessonSlug: lessonKey,
                        contentKey: lessonContentKey || null,
                    }),
                });
                if (!res.ok) return;
                const data = await res.json();
                if (annule || !data || !data.verdicts) return;
                setReviewVerdicts((prev) => ({ ...data.verdicts, ...prev }));
            } catch (e) {
                // silencieux : la pastille repart simplement de zéro
            }
        })();
        return () => {
            annule = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdminReviewer, reviewMode, lessonKey]);
    // Visiteur sans compte : il joue UNE leçon découverte, puis on l'invite à
    // créer un compte (le système d'énergie est 100 % serveur -> sans session,
    // aucune clé n'est dépensée, ce serait un accès illimité).
    const isAnon = status === "unauthenticated";
    // Retour = le parcours d'où vient la leçon (sinon le catalogue).
    const backHref = lesson.trackSlug
        ? {
              pathname: "/reps/path/[trackSlug]",
              params: { trackSlug: lesson.trackSlug },
          }
        : "/reps";
    // Chemin localisé (sans préfixe de locale) réinjecté dans /login?redirect=
    // pour ramener le membre là où il en était après connexion.
    let loginRedirect = "/reps";
    try {
        loginRedirect = getPathname({
            locale,
            href: backHref,
            forcePrefix: false,
        });
    } catch (e) {
        loginRedirect = "/reps";
    }

    const [queue, setQueue] = useState(() => steps.map((_, i) => i));
    const [visit, setVisit] = useState(0);
    // Clés : 1 clé = 1 leçon, dépensée AU DÉMARRAGE côté serveur. Plus de clé ->
    // on bloque avant de jouer (jamais en cours de leçon).
    const [keysState, setKeysState] = useState(null);
    const [keysBlocked, setKeysBlocked] = useState(false);
    // Animation de déverrouillage à l'ouverture (clés limitées) : { remaining, variant }.
    const [unlock, setUnlock] = useState(null);
    const [combo, setCombo] = useState(0);
    const [wrongSteps, setWrongSteps] = useState(() => new Set());
    /**
     * Nombre d'échecs par étape, et non « a échoué ou pas ».
     *
     * Une étape ratée repartait en fin de file, indéfiniment : un exercice
     * qu'on ne sait pas faire revenait à l'infini, sans jamais montrer sa
     * réponse. C'est le moment où l'on ferme l'onglet. Deux garde-fous :
     * dès le PREMIER échec la réponse s'affiche, et au DEUXIÈME l'étape ne
     * revient plus. On ne punit pas quelqu'un qui a vu la réponse et n'y
     * arrive toujours pas — la leçon reste comptée comme ratée sur cette
     * étape (wrongSteps), donc le récap dit la vérité.
     */
    const echecsRef = useRef({});
    const ECHECS_AVANT_DE_PASSER = 2;
    // Soupape du mode « insister » : une étape dont la réponse reste invisible
    // à l'apprenant boucle sans issue, alors elle finit malgré tout par sortir.
    const ECHECS_AVANT_DE_PASSER_SANS_ISSUE = 4;
    // Préférences d'APPRENTISSAGE (voir la réponse / insister jusqu'à la
    // bonne). Lues après l'hydratation : le premier rendu doit être identique
    // au serveur, qui n'a pas de localStorage.
    const [learnPrefs, setLearnPrefs] = useState(DEFAULT_LEARNING_PREFS);
    // Une bascule faite PENDANT que le fetch du compte est en vol prime sur
    // sa réponse : sans ce marqueur, la réponse périmée écraserait le choix
    // que l'apprenant vient de faire.
    const prefsToucheesRef = useRef(false);
    useEffect(() => {
        // Le cache local répond tout de suite, puis la vérité du COMPTE le
        // rattrape : un réglage activé sur l'app mobile vaut ici aussi.
        // Invité (401) ou panne : fetch rend null, le cache reste maître.
        setLearnPrefs(getLearningPrefs());
        let alive = true;
        fetchLearningPrefs().then((prefs) => {
            if (alive && prefs && !prefsToucheesRef.current) {
                setLearnPrefs(prefs);
            }
        });
        return () => {
            alive = false;
        };
    }, []);
    function updateLearnPrefs(patch) {
        prefsToucheesRef.current = true;
        setLearnPrefs((prev) => {
            const next = { ...prev, ...patch };
            saveLearningPrefs(next);
            return next;
        });
        // Le compte suit (fire-and-forget) : seul le changement voyage, pour
        // ne jamais écraser l'autre interrupteur réglé ailleurs.
        pushLearningPrefs(patch);
    }
    // Miroir ref des préférences : les callbacks audio (karaoké) vivent plus
    // longtemps qu'un rendu et doivent lire la valeur du moment, pas celle
    // capturée à la création de l'audio.
    const learnPrefsRef = useRef(learnPrefs);
    useEffect(() => {
        learnPrefsRef.current = learnPrefs;
    }, [learnPrefs]);
    const [answer, setAnswer] = useState(null);
    const [canCheck, setCanCheck] = useState(false);
    const [locked, setLocked] = useState(false);
    const [lastCorrect, setLastCorrect] = useState(null);
    // Arbitrage BeBot (contestation d'un échec sur write_code/python_code) :
    // un essai par étape, statut par index ({status, reason}).
    const [appeals, setAppeals] = useState({});
    const [flashId, setFlashId] = useState(0);
    // Réaction « bonne réponse » masquée quand le tirage n'a pas réagi
    // (ni bulle ni voix : cadre vert + jingle suffisent).
    const [hideOkBubble, setHideOkBubble] = useState(false);
    // Cadence des réactions : 1re bonne réponse = toujours, ensuite tirage
    // sans jamais deux réactions d'affilée.
    const okReactRef = useRef({ firstDone: false, lastFired: false });
    const [done, setDone] = useState(false);
    // Essai gratuit sans compte : `trialUsed` déclenche le mur d'inscription à
    // la PROCHAINE ouverture. Faux au 1er rendu (SSR + hydratation) pour ne pas
    // provoquer de mismatch ; l'effet ci-dessous lit le marqueur au montage.
    const [trialUsed, setTrialUsed] = useState(false);
    // Un seul appel de démarrage par montage : sans ce verrou, un re-rendu
    // rappellerait guest-start et dépenserait une deuxième clé pour la même
    // leçon (miroir de startAttempted côté app mobile).
    const guestStartRef = useRef(false);
    const [burst, setBurst] = useState(false);
    // Celebration de serie (streak) montree apres l'ecran de stats de fin.
    const [showStreak, setShowStreak] = useState(false);
    // Chiffre affiche par cet ecran, fige a son ouverture (voir le bouton).
    const [streakShown, setStreakShown] = useState(1);
    // Le serveur a explicitement refusé le submit (par opposition à « pas
    // encore répondu ») : rien n'a été crédité, on ne fête donc pas de série.
    const [submitFailed, setSubmitFailed] = useState(false);
    // Aperçu du navigateur replié : décidé par l'apprenant, gardé d'une
    // étape à l'autre, et remis à zéro à la leçon suivante (ce composant est
    // monté par leçon). Sur une leçon de code, deux colonnes dans la colonne
    // de lecture ne laissent pas la place d'écrire.
    // « both » (défaut), « code » ou « preview ». Décidé par l'apprenant, gardé
    // d'une étape à l'autre, remis à zéro à la leçon suivante (ce composant est
    // monté par leçon). Sur grand écran, il sert à replier l'aperçu pour
    // écrire ; sur écran étroit, à choisir entre empilé et onglets.
    const [previewMode, setPreviewMode] = useState("both");
    const previewValue = useMemo(
        () => ({ mode: previewMode, setMode: setPreviewMode }),
        [previewMode]
    );
    // Progression des quêtes puis déblocage du badge du mois : mêmes écrans que
    // l'app mobile, dans le même ordre (récap -> série -> quêtes -> badge).
    // Instantané des quêtes et du badge AVANT la leçon, pour le diff.
    const questsBeforeRef = useRef(null);
    const badgeBeforeRef = useRef(null);
    // Diff prêt à montrer, consommé une seule fois par la séquence de fin.
    const questsUpdateRef = useRef(null);
    // Pipeline de fin (submit + refetch des quêtes) : attendu, avec une grâce
    // bornée, quand l'apprenant appuie sur Continuer avant sa résolution.
    const submitPipelineRef = useRef(null);
    // Maillon « quêtes » de la séquence : franchi une seule fois.
    const questsPartRef = useRef(false);
    // Première leçon du jour, décidée en LOCAL à l'affichage du récap (null =
    // pas encore évalué). Pilote l'affichage de l'écran de série.
    const firstOfDayRef = useRef(null);
    const [questsCelebration, setQuestsCelebration] = useState(null);
    const [badgeCelebration, setBadgeCelebration] = useState(null);
    // Sortie en cours : le « Continuer » de la séquence de fin attend le
    // serveur, puis le chargement du parcours. C'est ce qui allume la barre de
    // progression DANS le bouton, à la place d'un écran qui semble figé.
    const [leaving, setLeaving] = useState(false);
    // Résultat serveur du submit de fin de leçon : { xpGained, streakCount } (vrais).
    const [serverResult, setServerResult] = useState(null);
    const [showExplain, setShowExplain] = useState(false);
    // Indice de l'étape (data.hint) : demandé par l'apprenant, AVANT de
    // répondre. Gratuit pour l'instant ; c'est ici qu'on branchera plus tard
    // un joker consommable (coffres).
    const [hintShown, setHintShown] = useState(false);
    const [showQuit, setShowQuit] = useState(false);
    // Chat BeBot (façon Brilliant) : panneau à gauche, la leçon passe dans une carte.
    // Le panneau reste monté (conversation persistante) ; on ne pilote que l'ouverture.
    const [chatOpen, setChatOpen] = useState(false);
    // API impérative du panneau (arbitrage affiché dans le chat).
    const assistantRef = useRef(null);
    const [showReviewIntro, setShowReviewIntro] = useState(false);
    const [reviewStarted, setReviewStarted] = useState(false);
    const [reviewMsg, setReviewMsg] = useState(REVIEW_CHEERS[0]);
    // Interstitiel BeBot a mi-parcours (personnage + bulle, une seule fois par lecon).
    const [showMidCheer, setShowMidCheer] = useState(false);
    const [midMsg, setMidMsg] = useState(MID_CHEERS[0]);
    const midShownRef = useRef(false);
    // Interstitiel BeBot de COMBO (serie de bonnes reponses) : peut apparaitre
    // sur plusieurs paliers dans une lecon, borne par le budget quotidien.
    const [showComboCheer, setShowComboCheer] = useState(false);
    const [comboMsg, setComboMsg] = useState(COMBO_CHEERS[0]);
    const [showReport, setShowReport] = useState(false);
    const [isMac, setIsMac] = useState(false);
    // Défi LÉGENDAIRE (?legendary=1, parité mobile) : 3 vies, barre or, aucune
    // aide post-verdict ; intro sombre AVANT le start (la clé n'est dépensée
    // qu'une fois l'intro passée), écran d'échec quand la 3e vie tombe.
    const searchParams = useSearchParams();
    const isLegendary = searchParams?.get("legendary") === "1";
    // Révision (CTA « Revoir » du path) : l'écran de fin promet l'XP de
    // révision, pas le plein tarif (parité mobile isReview/xpOverride).
    const isReview = !isLegendary && searchParams?.get("review") === "1";
    const [legendStarted, setLegendStarted] = useState(false);
    const [legendFailed, setLegendFailed] = useState(false);
    const [lives, setLives] = useState(LEGENDARY_LIVES);
    // Déjà réussi ? Lu APRÈS l'hydratation (localStorage) : la re-réussite
    // promet l'XP de révision, pas le plein tarif.
    const [wasLegendary, setWasLegendary] = useState(false);
    useEffect(() => {
        if (isLegendary) setWasLegendary(isLegendaryDone(lessonKey));
    }, [isLegendary, lessonKey]);
    // Serie CHAUDE cassee : courte secousse de l'eclair, puis retour au gris.
    // Detecte ici (transition chaud -> 0) plutot que dans le verdict : le
    // handler n'a pas a connaitre l'animation. Sous le seuil, rien : perdre une
    // serie de 2 ne merite pas d'etre puni visuellement.
    const prevComboRef = useRef(0);
    useEffect(() => {
        const was = prevComboRef.current;
        prevComboRef.current = combo;
        if (combo !== 0 || was < COMBO_HOT || reduce) return;
        boltControls.start({
            x: [0, -3, 3, -2, 2, 0],
            transition: { duration: 0.32, ease: "easeOut" },
        });
    }, [combo, boltControls, reduce]);
    // Etapes deja presentees : pour detecter le debut de la phase "revision des erreurs".
    const presentedRef = useRef(null);
    if (presentedRef.current === null) {
        presentedRef.current = new Set([0]);
    }
    // Reps LOT 2 : on mémorise la 1re réponse de chaque exercice pour la
    // re-correction serveur en fin de leçon (le serveur = source de vérité du
    // journal d'événements et, demain, de l'XP/énergie). startedRef garantit un
    // seul "start" par leçon.
    const answersRef = useRef({});
    const startedRef = useRef(false);
    // Le "start" a-t-il bien été enregistré côté serveur ? Le serveur exige un
    // vrai démarrage pour créditer (anti-rejeu) ; si le start initial a échoué
    // (réseau/erreur), on le rejoue avant le submit pour ne pas perdre le crédit.
    const startOkRef = useRef(false);
    const submittedRef = useRef(false); // garde anti double-submit (idempotence)

    // Temps actif passé sur la leçon (objectif quotidien du dashboard) :
    // on cumule le temps onglet visible, on met en pause quand il est caché,
    // et on envoie à la fin de la leçon + au démontage.
    const activeMsRef = useRef(0);
    const lastTickRef = useRef(null);
    const timeStoppedRef = useRef(false);

    const flushRepsTime = useCallback((useKeepalive) => {
        if (lastTickRef.current != null) {
            activeMsRef.current += Date.now() - lastTickRef.current;
            lastTickRef.current = timeStoppedRef.current ? null : Date.now();
        }
        const seconds = Math.floor(activeMsRef.current / 1000);
        if (seconds <= 0) return null;
        // On garde le reste sub-seconde pour ne rien perdre entre deux envois
        activeMsRef.current -= seconds * 1000;
        try {
            // Rendue pour que la fin de leçon puisse l'ATTENDRE : sinon
            // l'objectif du jour serait calculé sans le temps qu'on vient
            // de passer, et la barre ne bougerait pas.
            return fetch("/api/reps/time", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ timeInSeconds: seconds }),
                keepalive: !!useKeepalive,
            }).catch(() => {});
        } catch (e) {
            // hors-ligne : temps perdu, sans conséquence
        }
        return null;
    }, []);

    // On démarre la leçon côté serveur : ça DÉPENSE 1 clé (sauf illimité). Plus de
    // clé -> on bloque avant de jouer. Sinon on garde l'état clés.
    // Légendaire : la clé n'est dépensée qu'une fois l'intro passée —
    // ressortir de l'intro ne doit rien coûter (parité mobile).
    useEffect(() => {
        if (isLegendary && !legendStarted) return;
        if (startedRef.current) return;
        startedRef.current = true;
        (async () => {
            try {
                const res = await fetch("/api/reps/start", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        slug: lessonKey,
                        contentKey: lessonContentKey,
                    }),
                });
                const data = await res.json();
                if (!res.ok) return;
                if (data.blocked) {
                    setKeysState(data.keys || null);
                    setKeysBlocked(true);
                    return;
                }
                // Démarrage réellement enregistré côté serveur.
                startOkRef.current = true;
                if (data.keys) {
                    setKeysState(data.keys);
                }
                // Clés LIMITÉES : on joue le déverrouillage complet (la clé ouvre la
                // leçon) à CHAQUE ouverture. Abonnés (illimité) = aucune clé
                // dépensée -> pas d'animation.
                if (data.keys && !data.keys.unlimited) {
                    const rem =
                        typeof data.keys.keys === "number" ? data.keys.keys : 0;
                    setUnlock({ remaining: rem });
                }
            } catch (e) {
                // backend muet / hors-ligne : on laisse jouer (réconcilié au submit)
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonKey, isLegendary, legendStarted]);

    // Sortie de la séquence de fin : retour au parcours (ou au catalogue).
    const leaveToPath = useCallback(() => {
        // La barre du bouton reste allumée jusqu'au démontage : le parcours est
        // un composant serveur, son chargement dure. On ne repasse jamais à
        // false ici — c'est l'arrivée sur la page qui éteint tout.
        setLeaving(true);
        router.push(backHref);
        // backHref est dérivé du slug de la leçon, stable pour cet écran.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router]);

    // Badge du mois décroché PAR CETTE LEÇON : dernier écran avant la sortie.
    // « Déjà gagné avant » ne rejoue rien (sinon on le fêterait à chaque quête
    // du reste du mois).
    const showBadgeOrLeave = useCallback(() => {
        const badge = questsCelebration?.badge;
        if (
            badge &&
            badge.current?.isAwarded === true &&
            // hadBefore : on SAIT que le badge n'était pas encore gagné avant
            // cette leçon. Sans instantané fiable, on se tait — fêter un badge
            // décroché la semaine dernière serait pire que ne rien montrer.
            badge.hadBefore === true &&
            badge.awardedBefore !== true
        ) {
            setQuestsCelebration(null);
            setBadgeCelebration(badge.current);
            // Reps fête le badge ici : on le marque comme fêté pour que la fenêtre du
            // site (BadgeCelebrationWatcher) ne le rejoue pas à la sortie de la leçon.
            fetch("/api/badges/celebration", { method: "POST" }).catch(() => {});
            return;
        }
        // On NE vide PAS l'écran courant en sortant : router.push est
        // asynchrone, et l'écran démonté ferait réapparaître le récap de fin
        // (confettis compris) le temps que le parcours se charge.
        leaveToPath();
    }, [questsCelebration, leaveToPath]);

    // Quêtes qui ont avancé pendant la leçon. Consommation en une fois :
    // jamais montrées deux fois. Un clic rapide peut arriver avant la réponse
    // du serveur : on lui laisse une grâce BORNÉE (jamais d'attente réseau
    // ouverte — on préfère sauter l'écran plutôt que bloquer la sortie).
    const showQuestsOrLeave = useCallback(async () => {
        // Garde à un seul passage : pendant la grâce ci-dessous, un second
        // clic entrerait ici et, ne trouvant plus rien à montrer, sortirait
        // vers le parcours PAR-DESSUS l'écran de quêtes qui vient de s'ouvrir.
        if (questsPartRef.current) return;
        questsPartRef.current = true;
        if (questsUpdateRef.current == null && submitPipelineRef.current) {
            // Vraie attente réseau (jusqu'à QUESTS_GRACE_MS) : la barre du
            // bouton la rend visible au lieu de laisser croire à un clic perdu.
            setLeaving(true);
            await Promise.race([
                submitPipelineRef.current,
                new Promise((r) => setTimeout(r, QUESTS_GRACE_MS)),
            ]);
        }
        const update = questsUpdateRef.current;
        questsUpdateRef.current = null;
        if (update && update.deltas.length > 0) {
            // On reste dans la séquence : l'écran de quêtes ne doit pas
            // s'ouvrir avec son propre bouton déjà en train de charger.
            setLeaving(false);
            setShowStreak(false);
            setQuestsCelebration(update);
            return;
        }
        leaveToPath();
    }, [leaveToPath]);

    // Instantané des quêtes et du badge du mois AVANT la leçon : c'est le
    // point de comparaison de l'écran de progression. Best-effort et jamais
    // bloquant — sans lui, on saute simplement cet écran (la progression, elle,
    // est créditée par le serveur dans tous les cas).
    useEffect(() => {
        if (isAnon || status !== "authenticated") return;
        let alive = true;
        (async () => {
            const [quests, badges] = await Promise.all([
                fetchQuests(),
                fetchBadgeSummary(locale),
            ]);
            if (!alive) return;
            questsBeforeRef.current = quests;
            badgeBeforeRef.current = badges?.current ?? null;
        })();
        return () => {
            alive = false;
        };
    }, [isAnon, status, locale]);

    // Traceur de temps actif : démarre au montage, pause/reprise selon la
    // visibilité de l'onglet, envoi au démontage (keepalive pour survivre à
    // la navigation).
    useEffect(() => {
        lastTickRef.current = Date.now();
        const onVisibility = () => {
            if (document.hidden) {
                if (lastTickRef.current != null) {
                    activeMsRef.current += Date.now() - lastTickRef.current;
                    lastTickRef.current = null;
                }
            } else if (!timeStoppedRef.current) {
                lastTickRef.current = Date.now();
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            flushRepsTime(true);
        };
    }, [flushRepsTime]);

    useEffect(() => {
        try {
            setIsMac(
                /Mac|iPhone|iPad|iPod/.test(
                    navigator.platform || navigator.userAgent || ""
                )
            );
        } catch (e) {
            // ignore
        }
    }, []);
    const modKey = isMac ? "⌘" : "Ctrl";

    // Reglage son unifie (compte) : on/off + volume global (0..1), partage avec
    // les quiz, les coffres et les preferences via SoundContext. La voix BeBot
    // (verdicts parles) suit ses PROPRES reglages (on/off + volume dedies).
    const {
        soundEnabled,
        volume: soundVol,
        ready: soundReady,
        bebotVoiceEnabled,
        bebotPlaybackVolume,
    } = useSound();

    // Voix BeBot d'un verdict : phrase courte lue par ElevenLabs (résolue et
    // mise en cache CÔTÉ SERVEUR depuis une liste fermée, jamais de texte
    // libre). Un seul lecteur à la fois ; léger différé pour laisser le
    // jingle succès/erreur respirer. La voix est un bonus : tout échec est
    // silencieux, jamais bloquant.
    const voiceRef = useRef(null);
    // La LECTURE DE CONCEPT en cours, et elle seule : avancer d'écran la
    // coupe (on ne lit pas un texte qui n'est plus là), alors qu'un verdict
    // ou un indice, courts et attachés à l'action, finissent leur phrase.
    const conceptVoiceRef = useRef(null);
    useEffect(
        () => () => {
            if (voiceRef.current) {
                voiceRef.current.pause();
                voiceRef.current = null;
            }
        },
        []
    );
    // Une synthèse encore EN VOL à la fermeture du player ne doit jamais se
    // mettre à parler par-dessus la page suivante : ce serait une voix que
    // plus aucun réglage ne peut couper (le player qui la possédait est mort).
    // RÉARMÉ dans le corps de l'effet, pas seulement initialisé : en dev,
    // React Strict Mode joue effet -> nettoyage -> effet sur le même
    // composant ; un drapeau posé uniquement à la création resterait à false
    // après le nettoyage simulé, et TOUTES les voix du player se tairaient.
    const monteRef = useRef(true);
    useEffect(() => {
        monteRef.current = true;
        return () => {
            monteRef.current = false;
        };
    }, []);
    // Couper la voix BeBot (ou tous les sons) dans les réglages ARRÊTE la
    // phrase en cours ; le miroir ref bloque aussi une synthèse encore en vol
    // qui se résoudrait après la coupure.
    const verdictVoiceOnRef = useRef(true);
    useEffect(() => {
        const on = soundEnabled && bebotVoiceEnabled;
        verdictVoiceOnRef.current = on;
        if (!on && voiceRef.current) {
            voiceRef.current.pause();
            voiceRef.current = null;
        }
    }, [soundEnabled, bebotVoiceEnabled]);
    // Le volume suit en direct pendant que BeBot parle.
    useEffect(() => {
        if (voiceRef.current) {
            voiceRef.current.volume = bebotPlaybackVolume;
        }
    }, [bebotPlaybackVolume]);
    // BeBot DIT l'indice qu'il vient de donner (mêmes réglages que les
    // verdicts). Texte libre : la synthèse est mise en cache côté serveur par
    // empreinte du texte, donc chaque indice n'est fabriqué qu'une seule fois,
    // tous apprenants confondus.
    function speakHint(text) {
        if (!soundReady || !soundEnabled || !bebotVoiceEnabled) return;
        if (!hasRepsAssistant(sessionData?.user)) return;
        if (!text || !text.trim()) return;
        fetch("/api/reps/assistant/voice", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locale, text }),
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data || !data.url) return;
                if (!monteRef.current || !verdictVoiceOnRef.current) return;
                try {
                    if (voiceRef.current) voiceRef.current.pause();
                    const a = new Audio(data.url);
                    a.volume = bebotPlaybackVolume;
                    voiceRef.current = a;
                    a.play().catch(() => {});
                } catch (e) {
                    // la voix est un bonus
                }
            })
            .catch(() => {});
    }

    // KARAOKÉ DE LECTURE : pendant que BeBot lit un concept, les mots déjà lus
    // reprennent leur pleine opacité, façon Apple Music. Le rendu (RepsExercise)
    // numérote chaque mot en <span data-kw> ; le serveur fournit l'alignement
    // { total, times } où times[n] = [t0, t1] ou null (mot jamais lu : bloc de
    // code, au-delà du garde-fou de coût). Ici, AUCUNE règle de lecture : on
    // compare deux comptes et on suit une horloge.
    const karaokeRef = useRef(null);

    function stopKaraoke() {
        const k = karaokeRef.current;
        if (!k) return;
        if (k.raf) cancelAnimationFrame(k.raf);
        // Tout rallumer : la voix coupée (ou finie) ne doit jamais laisser un
        // écran à moitié éteint.
        for (const s of k.dimmed) {
            s.style.opacity = "";
        }
        karaokeRef.current = null;
    }

    // Un flux media qui casse EN COURS de lecture (réseau, stream avorté) peut
    // ne JAMAIS émettre pause/ended : Chrome reste en waiting/stalled avec un
    // currentTime figé. Sans ce délai de grâce, l'écran resterait atténué tant
    // que l'apprenant ne le quitte pas.
    const KARAOKE_GEL_MAX_MS = 5000;

    function startKaraoke(audio, alignment) {
        stopKaraoke();
        if (!learnPrefsRef.current?.readAlong) return;
        if (!alignment || !Array.isArray(alignment.times)) return;
        // L'écran courant : le body de concept rendu en dernier (une leçon n'en
        // affiche qu'un, mais une transition de sortie peut en laisser un mort).
        const roots = document.querySelectorAll("[data-kw-root]");
        const root = roots[roots.length - 1];
        if (!root) return;
        const spans = root.querySelectorAll("[data-kw]");
        // CLÉ DE SÛRETÉ : si le client ne compte pas les mêmes mots que le
        // serveur (parsing qui a divergé), on n'atténue RIEN. Un karaoké faux
        // est pire que pas de karaoké.
        if (spans.length !== alignment.total) return;
        const dimmed = [];
        const queue = []; // [t0, span], dans l'ordre de lecture
        spans.forEach((s, i) => {
            const t = alignment.times[i];
            if (!t || typeof t[0] !== "number") return; // jamais lu : intact
            s.style.transition = "opacity 0.35s ease-out";
            s.style.opacity = "0.45";
            dimmed.push(s);
            queue.push([t[0], s]);
        });
        if (!queue.length) return;
        queue.sort((a, b) => a[0] - b[0]);
        const k = {
            raf: 0,
            dimmed,
            queue,
            ptr: 0,
            lastT: -1,
            gelDepuis: performance.now(),
        };
        karaokeRef.current = k;
        const tick = () => {
            if (karaokeRef.current !== k) return;
            // 120 ms d'avance : le mot s'allume quand l'oreille l'attend, la
            // transition CSS absorbe le reste.
            const t = audio.currentTime + 0.12;
            while (k.ptr < k.queue.length && k.queue[k.ptr][0] <= t) {
                k.queue[k.ptr][1].style.opacity = "";
                k.ptr += 1;
            }
            // Tout est allumé : plus rien à piloter, la boucle s'éteint (la
            // voix, elle, finit sa phrase).
            if (k.ptr >= k.queue.length) {
                stopKaraoke();
                return;
            }
            // Position gelée sans pause : le flux est mort, on rend l'écran.
            if (audio.currentTime === k.lastT) {
                if (performance.now() - k.gelDepuis > KARAOKE_GEL_MAX_MS) {
                    stopKaraoke();
                    return;
                }
            } else {
                k.lastT = audio.currentTime;
                k.gelDepuis = performance.now();
            }
            k.raf = requestAnimationFrame(tick);
        };
        k.raf = requestAnimationFrame(tick);
    }

    // Préférence coupée en cours de lecture : on rend l'écran immédiatement.
    useEffect(() => {
        if (!learnPrefs.readAlong) stopKaraoke();
    }, [learnPrefs.readAlong]);
    // Démontage du player : jamais de boucle RAF orpheline.
    useEffect(() => () => stopKaraoke(), []);

    // BeBot LIT L'ÉCRAN DE CONCEPT. Réservé aux parcours dont l'admin a validé
    // le rendu (`voiceLessons`, réglé chemin par chemin dans /admin/reps) : un
    // parcours de marketing s'écoute bien, un parcours de code beaucoup moins.
    //
    // Contrairement à un indice ou à un verdict, ce texte est FIGÉ : la première
    // écoute paie la synthèse, toutes les autres tapent le cache R2. C'est ce
    // qui rend la lecture des leçons tenable à l'échelle du catalogue.
    function speakConcept(text) {
        if (!soundReady || !soundEnabled || !bebotVoiceEnabled) return;
        if (!lesson?.voiceLessons) return;
        if (!text || !text.trim()) return;
        // L'écran pour lequel cette voix est demandée : si l'apprenant a déjà
        // avancé quand la synthèse répond, l'audio peut encore jouer (règle
        // historique) mais le karaoké, lui, ne doit JAMAIS s'appliquer à un
        // autre écran — deux bodies différents peuvent compter le même nombre
        // de mots et déjouer la clé de sûreté.
        const ecranDemandeur = dernierConceptDitRef.current;
        fetch("/api/reps/lesson-voice", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locale, text }),
        })
            .then((r) => (r.ok ? r.json() : null))
            .then(async (data) => {
                if (!data || !data.url) return;
                // L'apprenant a pu couper le son, quitter la leçon ou changer
                // d'écran pendant la synthèse : la lecture d'un concept est
                // liée à SON écran (contrairement à un indice), une réponse
                // périmée ne joue pas — elle couperait la voix de l'écran
                // courant pour lire le texte du précédent par-dessus.
                if (!monteRef.current || !verdictVoiceOnRef.current) return;
                if (dernierConceptDitRef.current !== ecranDemandeur) return;
                // L'alignement vit sur R2 (public, immuable) : on le télécharge
                // seulement si le suivi de lecture est activé. Sans lui, la
                // voix passe telle quelle — le karaoké est un bonus du bonus.
                let alignment = null;
                if (data.alignmentUrl && learnPrefsRef.current?.readAlong) {
                    alignment = await fetch(data.alignmentUrl)
                        .then((r) => (r.ok ? r.json() : null))
                        .catch(() => null);
                }
                if (!monteRef.current || !verdictVoiceOnRef.current) return;
                if (dernierConceptDitRef.current !== ecranDemandeur) return;
                try {
                    if (voiceRef.current) voiceRef.current.pause();
                    stopKaraoke();
                    const a = new Audio(data.url);
                    a.volume = bebotPlaybackVolume;
                    voiceRef.current = a;
                    conceptVoiceRef.current = a;
                    if (alignment) {
                        // « playing » : l'atténuation ne précède jamais le son.
                        // « pause » couvre aussi la fin naturelle et la voix
                        // remplacée (un indice, un verdict) : tout se rallume.
                        a.addEventListener(
                            "playing",
                            () => {
                                if (
                                    dernierConceptDitRef.current !==
                                    ecranDemandeur
                                ) {
                                    return; // l'apprenant a changé d'écran
                                }
                                startKaraoke(a, alignment);
                            },
                            { once: true }
                        );
                        a.addEventListener("pause", stopKaraoke);
                        a.addEventListener("ended", stopKaraoke);
                        // Un flux qui casse émet 'error' (ou 'abort') SANS
                        // passer par pause/ended : l'écran doit se rallumer.
                        a.addEventListener("error", stopKaraoke);
                        a.addEventListener("abort", stopKaraoke);
                    }
                    a.play().catch(() => {});
                } catch (e) {
                    // la voix est un bonus
                }
            })
            .catch(() => {});
    }

    function speakVerdict(ok, flash) {
        if (!soundReady || !soundEnabled || !bebotVoiceEnabled) return;
        // Même palier que le chat BeBot : évite un appel voué au refus.
        if (!hasRepsAssistant(sessionData?.user)) return;
        const verdictKey = ok
            ? CORRECT_CHEERS[flash % CORRECT_CHEERS.length].replace(
                  "reps_",
                  ""
              )
            : "not_quite";
        fetch("/api/reps/assistant/voice", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locale, verdictKey }),
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data || !data.url) return;
                // Voix coupée pendant la synthèse : on ne démarre pas.
                if (!monteRef.current || !verdictVoiceOnRef.current) return;
                try {
                    if (voiceRef.current) voiceRef.current.pause();
                    const a = new Audio(data.url);
                    a.volume = bebotPlaybackVolume;
                    voiceRef.current = a;
                    setTimeout(() => {
                        // Re-vérifié au moment de PARTIR (le différé de 350 ms
                        // laisse le temps de couper).
                        if (!monteRef.current || !verdictVoiceOnRef.current)
                            return;
                        a.play().catch(() => {});
                    }, 350);
                } catch (e) {
                    // la voix est un bonus
                }
            })
            .catch(() => {});
    }

    // Sons BeBot (initialises cote client). On garde le volume "de base" de chaque son
    // et on applique le volume global a la lecture (pour conserver l'equilibre relatif).
    const sounds = useRef({});
    useEffect(() => {
        const mk = (src, base) => {
            const a = new Audio(src);
            a.preload = "auto";
            return { audio: a, base };
        };
        sounds.current = {
            success: mk("/static/sounds/bebot-success-quiz.mp3", 0.45),
            error: mk("/static/sounds/bebot-error-quiz.mp3", 0.45),
            click: mk("/static/sounds/bebot-click-quiz.mp3", 0.25),
            result: mk("/static/sounds/bebot-showsuccessresult-quiz.mp3", 0.5),
            streak: mk("/static/sounds/level-up.mp3", 0.27),
            secondChance: mk("/static/sounds/bubble-2.mp3", 0.45),
            middle: mk("/static/sounds/bubble.mp3", 0.5),
            drop: mk("/static/sounds/drop.mp3", 0.4),
            start: mk("/static/sounds/starting-interactive.mp3", 0.38),
            next: mk("/static/sounds/next-button.mp3", 0.4),
        };
    }, []);
    // Son de lancement : on attend que les preferences son soient chargees (ready),
    // sinon il partait avant qu'on sache si l'utilisateur a coupe le son. Une seule
    // fois — et jamais sur l'INTRO légendaire (le jingle part au COMMENCER).
    const startPlayedRef = useRef(false);
    useEffect(() => {
        if (!soundReady || startPlayedRef.current) return;
        if (isLegendary && !legendStarted) return;
        startPlayedRef.current = true;
        // Son de lancement de la lecon (peut etre bloque par l'autoplay -> .catch gere).
        play("start");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [soundReady, isLegendary, legendStarted]);
    function play(name) {
        if (!soundReady || !soundEnabled) return;
        const s = sounds.current[name];
        if (s && s.audio) {
            try {
                s.audio.volume = Math.max(0, Math.min(1, s.base * soundVol));
                s.audio.currentTime = 0;
                s.audio.play().catch(() => {});
            } catch (e) {
                // ignore
            }
        }
    }
    function haptic(p) {
        try {
            if (navigator.vibrate) navigator.vibrate(p);
        } catch (e) {
            // ignore
        }
    }
    function handleTile() {
        play("click");
        haptic(8);
    }
    // Depose d'un bloc dans un emplacement (drag-and-drop) : son dedie.
    function handleDrop() {
        play("drop");
        haptic(12);
    }

    // Pour le motif "la bonne reponse est comptee fausse" : decrit la question,
    // la reponse donnee et la reponse attendue, de facon lisible selon le type.
    function describeAnswers() {
        const out = { question: "", userAnswer: "", expectedAnswer: "" };
        if (!ex) return out;
        out.question = ex.prompt || (ex.data && ex.data.body) || "";
        const sol = ex.solution || {};
        const data = ex.data || {};
        const join = (a) =>
            Array.isArray(a) ? a.join(" ") : a == null ? "" : String(a);
        switch (ex.type) {
            case "mcq": {
                const opts = data.options || [];
                out.userAnswer =
                    answer != null && opts[answer] != null
                        ? opts[answer]
                        : "(aucune réponse)";
                out.expectedAnswer =
                    opts[sol.correctIndex] != null ? opts[sol.correctIndex] : "";
                break;
            }
            case "multi_select": {
                // Le repli générique ne dirait que des index : BeBot a besoin
                // des libellés pour nommer ce qui manquait ou ce qui était en trop.
                const opts = data.options || [];
                const label = (i) => (opts[Number(i)] != null ? opts[Number(i)] : `#${i}`);
                out.userAnswer = Array.isArray(answer)
                    ? answer.map(label).join("  +  ")
                    : "(aucune réponse)";
                out.expectedAnswer = Array.isArray(sol.correctIndexes)
                    ? sol.correctIndexes.map(label).join("  +  ")
                    : "";
                break;
            }
            case "tap_tokens":
                out.userAnswer = join(answer);
                out.expectedAnswer = join(sol.sequence);
                break;
            case "fill_blank":
                out.userAnswer = join(answer);
                out.expectedAnswer = join(sol.blanks);
                break;
            case "reorder":
                out.userAnswer = join(answer);
                out.expectedAnswer = join(sol.order);
                break;
            case "drop_blocks": {
                const blocks = data.blocks || [];
                const toStr = (arr) =>
                    Array.isArray(arr)
                        ? arr
                              .map((bi) => (bi != null ? blocks[bi] : "·"))
                              .join(" ")
                        : "";
                out.userAnswer = toStr(answer);
                out.expectedAnswer = toStr(
                    sol.slots || (sol.accept && sol.accept[0]) || []
                );
                break;
            }
            case "type_answer":
                out.userAnswer = join(answer);
                out.expectedAnswer = (sol.accepted || []).join("  OU  ");
                break;
            case "write_code":
            case "python_code":
            case "js_code": {
                // La correction du code se lit en MOTIFS (include/forbid, sortie
                // console, règles de structure). Ces motifs ne sont plus affichés
                // à l'apprenant : ils sont normalisés pour le grader et se lisent
                // comme du code cassé. On les décrit ici pour BeBot et pour le
                // signalement, en gardant les noms du format de contenu (neutres
                // en langue) plutôt qu'un JSON brut.
                const motifs = (arr) =>
                    (Array.isArray(arr) ? arr : [])
                        // Une entrée peut être un TABLEAU : « au moins une de
                        // ces écritures » (background / background-color).
                        .map((m) =>
                            Array.isArray(m) ? m.join(" OU ") : String(m || "")
                        )
                        .filter(Boolean);
                const parts = [];
                const inc = motifs(sol.include);
                if (inc.length) parts.push("include : " + inc.join("  +  "));
                const bad = motifs(sol.forbid);
                if (bad.length) parts.push("forbid : " + bad.join("  +  "));
                if (sol.stdout) parts.push("stdout : " + String(sol.stdout));
                if (Array.isArray(sol.dom) && sol.dom.length)
                    parts.push("dom : " + JSON.stringify(sol.dom));
                if (sol.files && typeof sol.files === "object")
                    parts.push("files : " + JSON.stringify(sol.files));
                out.userAnswer =
                    answer && typeof answer === "object"
                        ? answer.files && typeof answer.files === "object"
                            ? Object.entries(answer.files)
                                  .map(([n, c]) => `── ${n} ──\n${c}`)
                                  .join("\n")
                            : join(answer.code)
                        : join(answer);
                out.expectedAnswer = parts.join("\n");
                break;
            }
            default:
                out.userAnswer = join(answer);
                out.expectedAnswer = JSON.stringify(sol);
        }
        return out;
    }

    // Motifs de signalement (ID stable + libelle i18n). custom = champ texte libre.
    const reportReasons = [
        { id: "wrong", label: t("reps_report_reason_wrong") },
        { id: "unclear", label: t("reps_report_reason_unclear") },
        { id: "error", label: t("reps_report_reason_error") },
        { id: "other", label: t("reps_report_reason_other"), custom: true },
    ];

    // Signaler un exercice : envoi d'un email a l'equipe via /api/reps/report.
    // L'utilisateur (email + prenom/nom) est ajoute cote serveur depuis la session.
    // La modale partagee gere l'UI ; ici on construit le payload et on jette en cas
    // d'echec (l'ID du motif est stable, independant de la langue).
    async function submitReport(reasonId, customMessage) {
        const payload = {
            reason: reasonId,
            // Code canonique : identifie la leçon quelle que soit la langue.
            lessonSlug: lessonKey,
            lessonTitle: lesson.title,
            // Langue du contenu signalé : dit à l'équipe QUELLE version corriger
            // (un signalement EN = corriger le fichier miroir translations/en/...).
            contentLocale,
            stepIndex: cur,
            exerciseType: ex && ex.type,
            // Identité exacte du step servi (lesson/get) : sur une leçon de
            // rappel, la position seule désignerait un autre exercice.
            exerciseId: (ex && ex.id) || null,
            // Contexte d'affichage, pour instruire un bug d'éditeur sans
            // interroger l'apprenant : densité de pixels, zoom, taille de
            // fenêtre, système, et chemin de saisie Monaco (EditContext sur
            // Chrome/Edge, textarea ailleurs). Le navigateur lui-même est lu
            // côté route (User-Agent).
            client:
                typeof window === "undefined"
                    ? null
                    : {
                          dpr: window.devicePixelRatio || 1,
                          zoom:
                              (window.visualViewport && window.visualViewport.scale) || 1,
                          viewport: `${window.innerWidth}x${window.innerHeight}`,
                          platform:
                              (navigator.userAgentData && navigator.userAgentData.platform) ||
                              navigator.platform ||
                              null,
                          editContext: typeof window.EditContext === "function",
                      },
        };
        if (reasonId === "wrong") {
            const d = describeAnswers();
            payload.question = d.question;
            payload.userAnswer = d.userAnswer;
            payload.expectedAnswer = d.expectedAnswer;
        }
        if (reasonId === "other") {
            payload.customMessage = customMessage || "";
        }
        const res = await fetch("/api/reps/report", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("report failed");
    }

    useEffect(() => {
        if (!done) return;
        play("result");
        // Première leçon du jour ? Évalué ICI, AVANT de poser le marqueur, et
        // sur le navigateur : l'écran de série ne doit pas dépendre d'une
        // réponse serveur qui peut être lente ou perdue (parité mobile, où
        // c'est le store de l'appareil qui tranche). Le serveur reste maître
        // du CHIFFRE, le local décide seulement s'il faut célébrer.
        if (!isAnon && firstOfDayRef.current === null) {
            firstOfDayRef.current = !hasDoneRepsToday();
            markRepsActivityToday();
        }
        // Leçon terminée : on fige et on envoie le temps actif accumulé.
        // L'écran de résultat/célébration ne compte pas comme du temps d'apprentissage.
        let timeFlush = null;
        if (!timeStoppedRef.current) {
            timeStoppedRef.current = true;
            timeFlush = flushRepsTime(false);
            lastTickRef.current = null;
        }
        // Enregistrement serveur autoritaire (UN appel) : re-correction + journal,
        // énergie, XP (league auto), quête, streak. clientEventId = idempotence ;
        // clientTimestamp = jour local pour le streak. Garde anti double-submit.
        if (!submittedRef.current) {
            submittedRef.current = true;
            // Promesse gardée : la séquence de fin l'attend, avec une grâce
            // bornée, avant de décider s'il y a des quêtes à montrer.
            submitPipelineRef.current = (async () => {
                try {
                    // Filet de sécurité : si le démarrage initial n'a pas pu être
                    // enregistré côté serveur (réseau/erreur -> on avait "laissé
                    // jouer"), on le rejoue AVANT de soumettre. Le serveur exige un
                    // vrai "start" pour créditer (anti-rejeu) : sans ça, une leçon
                    // pourtant terminée ne gagnerait pas son XP.
                    if (!startOkRef.current) {
                        try {
                            const sres = await fetch("/api/reps/start", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                    slug: lessonKey,
                                    contentKey: lessonContentKey,
                                }),
                            });
                            const sdata = await sres.json().catch(() => null);
                            if (sres.ok && sdata && !sdata.blocked) {
                                startOkRef.current = true;
                            }
                        } catch (e) {
                            // toujours hors-ligne : le crédit se fera au prochain redo
                        }
                    }

                    // Le temps de la leçon doit être enregistré AVANT le
                    // submit : c'est lui qui alimente l'objectif du jour
                    // renvoyé dans la réponse. Attente BORNÉE : un
                    // /api/reps/time qui pendrait (verrou en base, réseau qui
                    // ne rend jamais la main) empêcherait le submit de partir,
                    // donc la leçon ne serait jamais créditée. L'objectif du
                    // jour est un bonus, le crédit ne doit pas en dépendre.
                    if (timeFlush) {
                        try {
                            await Promise.race([
                                timeFlush,
                                new Promise((r) =>
                                    setTimeout(r, TIME_FLUSH_GRACE_MS)
                                ),
                            ]);
                        } catch (e) {
                            // temps perdu : l'objectif sera juste en retard
                        }
                    }

                    const answers = Object.keys(answersRef.current).map(
                        (p) => ({
                            position: Number(p),
                            answer: answersRef.current[p],
                        })
                    );
                    const clientEventId = `${lessonKey}-${Date.now()}-${Math.random()
                        .toString(36)
                        .slice(2, 10)}`;
                    let r;
                    try {
                        r = await fetch("/api/reps/submit", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                slug: lessonKey,
                                contentKey: lessonContentKey,
                                // Locale du contenu joué : le serveur corrige contre
                                // les solutions de CETTE langue (jamais la locale d'UI).
                                contentLocale,
                                answers,
                                clientEventId,
                                clientTimestamp: new Date().toISOString(),
                            }),
                        });
                    } catch (networkError) {
                        // Panne de TRANSPORT : la requête n'est jamais partie
                        // (hors ligne, DNS, connexion coupée). C'est le mode
                        // d'échec le plus courant, et le seul catch du bloc
                        // étant muet, il laissait la série se faire fêter avec
                        // un chiffre déduit du client. Le web n'a pas de file
                        // de rejeu comme l'app : ce crédit est perdu, on ne
                        // l'annonce pas. Une requête simplement LENTE, elle, ne
                        // passe pas ici et garde sa célébration optimiste.
                        setSubmitFailed(true);
                        return;
                    }
                    const data = r.ok ? await r.json() : null;
                    if (!data) {
                        // Submit refusé : rien n'a été crédité côté serveur.
                        // Le signaler explicitement, car `serverResult` reste
                        // null aussi quand la réponse est simplement en route,
                        // et cette nuance décide si on a le droit de fêter la
                        // série (voir celebrateStreak).
                        setSubmitFailed(true);
                        return;
                    }
                    setServerResult({
                        xpGained:
                            typeof data.xpGained === "number"
                                ? data.xpGained
                                : null,
                        streakCount:
                            typeof data.streakCount === "number"
                                ? data.streakCount
                                : null,
                        // Vrai UNIQUEMENT à la 1re leçon du jour (la série grimpe) :
                        // l'écran de série ne s'affiche alors qu'une fois par jour.
                        streakGrew: data.streakGrew === true,
                        // { goalMinutes, minutesToday, reached } ou null si
                        // l'apprenant n'a pas d'objectif (onboarding ancien).
                        dailyGoal: data.dailyGoal || null,
                    });
                    // Quêtes : Symfony les fait avancer AVANT de répondre au
                    // submit, donc un refetch lancé maintenant est à jour. On
                    // compare à l'instantané pris au montage pour savoir quoi
                    // raconter (rien à montrer sans instantané : jamais
                    // bloquant, l'écran est un bonus).
                    if (data.duplicate !== true && data.credited !== false) {
                        const before = questsBeforeRef.current;
                        if (before) {
                            try {
                                const [after, badges] = await Promise.all([
                                    fetchQuests(),
                                    fetchBadgeSummary(locale),
                                ]);
                                questsUpdateRef.current = buildQuestsUpdate(
                                    before,
                                    after,
                                    badges?.current ?? null,
                                    badgeBeforeRef.current
                                );
                            } catch (e) {
                                // pas d'écran de quêtes : la progression est
                                // créditée quoi qu'il arrive.
                            }
                        }
                    }
                } catch (e) {
                    // ignore
                } finally {
                    // Progression + streak + XP sont serveur : on rafraîchit la
                    // session pour que la barre du haut se mette à jour. Dans un
                    // `finally` : les sorties anticipées (submit refusé) la
                    // laissaient sinon sur son instantané périmé, et la barre du
                    // haut gardait un compteur faux jusqu'au rechargement.
                    if (updateSession) updateSession();
                }
            })();
        }
        const t = setTimeout(() => setBurst(true), 380);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [done, lessonKey]);

    // NAVIGATION LIBRE du mode revue : feuilleter les steps comme des pages,
    // sans répondre. La file, les réponses et la complétion ne bougent pas —
    // seul le step AFFICHÉ change. reviewBrowse = null en jeu normal, sinon
    // l'index feuilleté.
    const reviewBrowsing =
        reviewBrowse !== null && isAdminReviewer && reviewMode && !done;
    const cur = reviewBrowsing ? reviewBrowse : queue[0];
    const ex = steps[cur];
    const isConcept = ex && ex.type === "concept";

    // Envoi d'un verdict de revue sur l'étape COURANTE. Optimiste : le bouton
    // se colore tout de suite, et se décolore si le serveur refuse.
    async function sendReviewVerdict(verdict, motif, note) {
        const stepIndex = cur;
        const stepEx = ex;
        if (stepIndex === undefined || !stepEx || reviewSending) return;
        setReviewSending(true);
        setReviewVerdicts((v) => ({ ...v, [stepIndex]: verdict }));
        try {
            const res = await fetch("/api/reps/review", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    verdict,
                    motif: motif || null,
                    note: note || null,
                    lessonSlug: lessonKey,
                    contentKey: lessonContentKey || null,
                    // Identité exacte du step servi (lesson/get) : survit aux
                    // renumérotages et aux tirages des leçons de rappel.
                    exerciseId: (stepEx && stepEx.id) || null,
                    stepIndex,
                    contentLocale,
                }),
            });
            if (!res.ok) throw new Error("review verdict failed");
        } catch (e) {
            console.error("Reps review verdict:", e);
            setReviewVerdicts((v) => {
                const next = { ...v };
                delete next[stepIndex];
                return next;
            });
        } finally {
            setReviewSending(false);
        }
    }
    function openReviewKo() {
        setReviewMotif(null);
        setReviewNote("");
        setShowReviewKo(true);
    }
    // Se déplacer en navigation libre : l'état transitoire de l'étape quittée
    // (réponse en cours, verdict, explication) ne doit pas suivre — le step
    // d'arrivée se présente comme à la première visite.
    function reviewGoTo(index) {
        if (!steps.length) return;
        const i = Math.min(steps.length - 1, Math.max(0, index));
        setReviewBrowse(i);
        setShowExplain(false);
        setAnswer(null);
        setCanCheck(false);
        setLocked(false);
    }
    // FEUILLETER D'UNE LEÇON À L'AUTRE. Arrivé au bout d'une leçon, « Suivant »
    // ouvre la leçon d'après (et « Précédent », au début, celle d'avant) :
    // une session de revue enchaîne les leçons sans repasser par le chemin.
    // Le feuilletage est mémorisé pour que la leçon ouverte s'affiche déjà
    // feuilletée, sur son premier écran ou sur son dernier selon le sens.
    function memoriserFeuilletage(valeur) {
        try {
            if (valeur) window.localStorage.setItem("reps_review_browse", valeur);
            else window.localStorage.removeItem("reps_review_browse");
        } catch (e) {
            // stockage indisponible : le feuilletage ne survivra pas au changement de leçon
        }
    }
    function reviewOuvrirLecon(slug, sens) {
        if (!slug) return;
        memoriserFeuilletage(sens === "prev" ? "fin" : "debut");
        router.push({ pathname: "/reps/[lessonSlug]", params: { lessonSlug: slug } });
    }
    function toggleReviewBrowse() {
        if (reviewBrowse !== null) {
            // Retour au jeu normal : la file reprend là où elle en était.
            memoriserFeuilletage(null);
            setReviewBrowse(null);
            setShowExplain(false);
            setAnswer(null);
            setCanCheck(false);
            setLocked(false);
        } else {
            memoriserFeuilletage("debut");
            reviewGoTo(typeof cur === "number" ? cur : 0);
        }
    }
    // Leçon ouverte depuis une leçon feuilletée : on reprend le feuilletage.
    useEffect(() => {
        if (!isAdminReviewer || !reviewMode || typeof window === "undefined") return;
        let memo = null;
        try {
            memo = window.localStorage.getItem("reps_review_browse");
        } catch (e) {
            memo = null;
        }
        if (!memo || !steps.length) return;
        setReviewBrowse(memo === "fin" ? steps.length - 1 : 0);
        if (memo === "fin") memoriserFeuilletage("debut");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdminReviewer, reviewMode, lessonKey]);
    const reviewAuDebut = (typeof cur === "number" ? cur : 0) <= 0;
    const reviewAuBout = (typeof cur === "number" ? cur : 0) >= steps.length - 1;
    function reviewPrecedent() {
        if (reviewAuDebut) reviewOuvrirLecon(lesson.prevLessonSlug, "prev");
        else reviewGoTo(cur - 1);
    }
    function reviewSuivant() {
        if (reviewAuBout) reviewOuvrirLecon(lesson.nextLessonSlug, "next");
        else reviewGoTo(cur + 1);
    }
    reviewRef.current = {
        active: isAdminReviewer && reviewMode && !done,
        browsing: reviewBrowsing,
        ok: () => sendReviewVerdict("ok"),
        ko: openReviewKo,
        prev: reviewPrecedent,
        next: reviewSuivant,
    };

    // Version affichable de la solution pour le panneau du feuilletage :
    // complète le mécanisme de « Voir la solution » pour les types que la
    // modale n'affiche jamais (réponse peinte par le verdict, ou repli
    // générique qui ne parle qu'en index).
    function reponseRevueLignes() {
        if (!ex || !ex.solution) return null;
        const d = ex.data || {};
        const sol = ex.solution;
        if (
            (ex.type === "mcq" || ex.type === "predict_output") &&
            Number.isInteger(sol.correctIndex) &&
            Array.isArray(d.options)
        )
            return [d.options[sol.correctIndex]];
        if (
            ex.type === "multi_select" &&
            Array.isArray(sol.correctIndexes) &&
            Array.isArray(d.options)
        )
            return sol.correctIndexes.map((i) => d.options[i]);
        if (
            ex.type === "tap_target" &&
            Number.isInteger(sol.index) &&
            Array.isArray(d.parts) &&
            d.parts[sol.index] != null
        )
            return [`\`${String(d.parts[sol.index]).trim()}\``];
        if (ex.type === "reorder" && Array.isArray(sol.order) && Array.isArray(d.items))
            return sol.order.map((i, rang) => `${rang + 1}. ${d.items[i]}`);
        if (
            ex.type === "drag_drop" &&
            Array.isArray(sol.placement) &&
            Array.isArray(d.items) &&
            Array.isArray(d.zones)
        )
            return d.zones.map(
                (z, zi) =>
                    `${z.label} : ` +
                    d.items.filter((_, ii) => sol.placement[ii] === zi).join(" · ")
            );
        if (
            ex.type === "match_pairs" &&
            Array.isArray(sol.pairs) &&
            Array.isArray(d.left) &&
            Array.isArray(d.right)
        )
            return sol.pairs.map(([g, dr]) => `${d.left[g]} : ${d.right[dr]}`);
        // write_code sans rendu-objectif : on montre les MOTIFS que le
        // correcteur exige, fichier par fichier — c'est la vraie solution du
        // point de vue du relecteur.
        if (
            (ex.type === "write_code" || ex.type === "python_code" || ex.type === "js_code") &&
            !(d.target && String(d.target))
        ) {
            const versLigne = (nom, regles) => {
                const parts = [];
                for (const inc of regles.include || [])
                    parts.push(Array.isArray(inc) ? inc.join(" ou ") : String(inc));
                for (const r of regles.dom || []) {
                    let p = String(r.selector || "");
                    if (r.count != null) p += ` ×${r.count}`;
                    else if (r.min != null) p += ` (au moins ${r.min})`;
                    if (r.contains) p += ` « ${r.contains} »`;
                    parts.push(p);
                }
                for (const fb of regles.forbid || []) parts.push(`interdit : ${fb}`);
                if (regles.stdout) parts.push(`sortie : ${regles.stdout}`);
                return (nom ? `\`${nom}\` : ` : "") + parts.join("  ·  ");
            };
            if (sol.files && typeof sol.files === "object")
                return Object.entries(sol.files).map(([nom, regles]) =>
                    versLigne(nom, regles || {})
                );
            if (sol.include || sol.dom || sol.forbid || sol.stdout)
                return [versLigne(null, sol)];
        }
        return null;
    }

    // VALIDATION IMPLICITE : terminer une leçon en mode revue valide d'un
    // coup tous les steps restés sans verdict — la couverture devient
    // complète sans un geste de plus. Les X posés restent des X, les V
    // explicites gardent leur sens (« regardé de près ») : côté serveur,
    // chaque ligne implicite est marquée comme telle. Un seul envoi groupé.
    const reviewImplicitSentRef = useRef(false);
    useEffect(() => {
        if (!done || reviewImplicitSentRef.current) return;
        if (!isAdminReviewer || !reviewMode) return;
        reviewImplicitSentRef.current = true;
        const restants = steps
            .map((s, i) => ({ stepIndex: i, exerciseId: (s && s.id) || null }))
            .filter(({ stepIndex }) => !(stepIndex in reviewVerdicts));
        if (!restants.length) return;
        fetch("/api/reps/review", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                verdict: "ok",
                lessonSlug: lessonKey,
                contentKey: lessonContentKey || null,
                contentLocale,
                batch: restants,
            }),
        }).catch((e) => console.error("Reps review implicite:", e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [done]);

    // BeBot lit le concept à l'arrivée sur l'écran, une seule fois par écran.
    // Volontairement limité aux CONCEPTS : un énoncé d'exercice se lit en même
    // temps qu'on manipule, et une voix par-dessus gênerait plus qu'elle
    // n'aiderait. La bulle de BeBot, elle, garde sa propre voix.
    const dernierConceptDitRef = useRef(null);
    useEffect(() => {
        // Changement d'écran : la boucle du karaoké ne doit pas survivre à un
        // body démonté (elle tournerait sur des nœuds détachés), et la LECTURE
        // du concept quitté s'arrête — BeBot ne lit pas un texte qui n'est
        // plus à l'écran. Un verdict ou un indice, eux, finissent leur phrase.
        stopKaraoke();
        if (conceptVoiceRef.current) {
            conceptVoiceRef.current.pause();
            conceptVoiceRef.current = null;
        }
        if (!isConcept || done) return;
        // Un interstitiel (mi-parcours, combo, intro de révision) couvre
        // l'écran : le concept n'est pas encore devant les yeux, on ne lit
        // pas un texte invisible. L'écran n'est PAS marqué comme dit — les
        // deps rejouent l'effet à la fermeture du cheer, et la lecture (avec
        // son karaoké) démarre quand le texte apparaît réellement.
        if (showMidCheer || showComboCheer || showReviewIntro) return;
        const cle = `${lessonKey}:${cur}`;
        if (dernierConceptDitRef.current === cle) return;
        dernierConceptDitRef.current = cle;
        speakConcept(ex?.data?.body);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cur,
        isConcept,
        done,
        lessonKey,
        showMidCheer,
        showComboCheer,
        showReviewIntro,
    ]);
    const resolvedCount = total - queue.length;
    // Légendaire : aucune aide post-verdict (pas d'explication, comme mobile).
    const hasExplain = locked && ex && !!ex.explanation && !isLegendary;

    /**
     * LA RÉPONSE, telle qu'on peut la MONTRER à l'apprenant.
     *
     * `describeAnswers().expectedAnswer` sert BeBot et les signalements : pour
     * du code, il rend les motifs de correction normalisés, qui se lisent
     * comme du code cassé. On ne montre donc ce champ que pour les types dont
     * la réponse est une vraie phrase ou une vraie suite de mots. Pour un
     * exercice de code, la seule réponse présentable est `data.target`, le
     * document attendu — et quand il manque, on ne montre rien plutôt que de
     * lâcher des motifs illisibles. L'étape se passera quand même.
     */
    const reponseMontrable = useMemo(() => {
        if (!ex || isLegendary) return null;
        // Les types dont le verdict peint déjà la bonne réponse n'ont rien de
        // plus à offrir : un bouton « Voir la solution » par-dessus serait une
        // deuxième révélation de la même chose. L'encadré vert EST la solution.
        if (TYPES_REPONSE_PEINTE.includes(ex.type)) return null;
        const estDuCode =
            ex.type === "write_code" || ex.type === "python_code" || ex.type === "js_code";
        if (estDuCode) {
            const cible = ex.data && ex.data.target;
            return cible
                ? { kind: "code", lang: "html", value: String(cible) }
                : null;
        }
        // Un tune se montre par sa DÉCLARATION remplie avec les valeurs de la
        // solution : « p { padding: 16px; margin: 28px; } », jamais le JSON
        // brut de solution.values.
        if (ex.type === "tune") {
            const decl = ex.data && ex.data.declaration;
            const vals = (ex.solution && ex.solution.values) || {};
            if (!decl) return null;
            let out = String(decl);
            for (const [k, v] of Object.entries(vals)) {
                out = out.split(`{${k}}`).join(String(v));
            }
            return { kind: "code", lang: "css", value: out };
        }
        const attendu = describeAnswers().expectedAnswer;
        // Le repli JSON.stringify des types inconnus ne se montre JAMAIS.
        if (!attendu || /^[[{]/.test(String(attendu).trim())) return null;
        return { kind: "texte", value: String(attendu) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ex, isLegendary, answer]);

    // Ce que l'apprenant peut VOIR de la réponse, par un moyen ou par l'autre :
    // la modale « Voir la solution », ou le verdict qui la peint de lui-même.
    const solutionAtteignable =
        !!reponseMontrable || (!!ex && TYPES_REPONSE_PEINTE.includes(ex.type));

    // La solution ne s'affiche plus toute seule : un bouton l'OFFRE, et une
    // modale la montre proprement (coloration, espace). Par défaut elle n'est
    // proposée qu'au DEUXIÈME échec — on pousse à chercher d'abord — sauf si
    // l'apprenant a activé « dès la première erreur » dans ses réglages.
    const seuilSolution = learnPrefs.showAnswers ? 1 : 2;
    const proposerSolution =
        locked &&
        !lastCorrect &&
        !isLegendary &&
        !!reponseMontrable &&
        (echecsRef.current[cur] || 0) >= seuilSolution;
    const [showSolution, setShowSolution] = useState(false);
    useEffect(() => {
        if (!locked) setShowSolution(false);
    }, [locked]);

    /**
     * L'ÉTAPE DÉBORDE-T-ELLE SOUS LA BARRE D'ACTIONS ?
     *
     * Sur un écran peu haut, la fin du contenu passe derrière « Vérifier » /
     * « Continuer » : c'est inévitable, mais rien ne dit à l'apprenant que ça
     * continue en dessous. On mesure donc ce qu'il reste à faire défiler pour
     * poser une ombre douce contre la barre, et on la retire dès le bas atteint.
     */
    const [stepOverflow, setStepOverflow] = useState(false);
    const scrollerRef = useRef(null);
    // Écran intercalaire (félicitations de mi-parcours, intro de révision,
    // combo) : UNE seule définition, lue par la mesure de débordement ET par le
    // rendu. En deux exemplaires, un quatrième intercalaire ajouté d'un seul
    // côté aurait suffi à désaccorder l'ombre du contenu qu'elle annonce.
    const interstitial = showMidCheer || showReviewIntro || showComboCheer;
    // AnimatePresence garde l'étape sortante le temps de son fondu : son
    // détachement arrive APRÈS l'arrivée de la nouvelle, on ignore donc les
    // null pour ne pas perdre le conteneur qui vient de s'installer.
    const attachScroller = useCallback((node) => {
        if (node) scrollerRef.current = node;
    }, []);
    useEffect(() => {
        const el = scrollerRef.current;
        // isConnected, et pas seulement « el existe » : quand le player passe à
        // l'écran de fin, le conteneur d'étape est démonté sans que la ref le
        // sache, et on brancherait des écouteurs sur un nœud sorti du document.
        if (!el || !el.isConnected) {
            setStepOverflow(false);
            return;
        }
        const measure = () => {
            // Quelques pixels de marge : sans elle, des hauteurs sub-pixel
            // laisseraient l'ombre clignoter alors qu'on est déjà en bas.
            setStepOverflow(
                el.scrollHeight - el.scrollTop - el.clientHeight > 6
            );
        };
        measure();
        el.addEventListener("scroll", measure, { passive: true });
        window.addEventListener("resize", measure);
        // Le contenu grandit EN COURS d'étape (un aperçu qu'on déplie, une
        // explication qui s'ouvre) : on observe le conteneur pour la fenêtre
        // qui bouge, et son enfant pour le contenu qui pousse.
        let ro = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            ro.observe(el);
            if (el.firstElementChild) ro.observe(el.firstElementChild);
        }
        return () => {
            el.removeEventListener("scroll", measure);
            window.removeEventListener("resize", measure);
            if (ro) ro.disconnect();
        };
        // visit : chaque étape remonte un NOUVEAU conteneur à mesurer.
        // interstitial : les félicitations remontent le leur, à mesurer aussi —
        // un long message de fin coupé en bas mérite le même indice qu'une étape.
        // done : l'écran de fin démonte le conteneur, il faut le débrancher.
    }, [visit, interstitial, done]);

    // État du groupe d'actions de la barre du bas (façon Brilliant) : sert de
    // key au groupe (remontage animé à chaque bascule) ET de déclencheur de la
    // restauration du focus ci-dessous.
    const actionStage =
        showMidCheer || showReviewIntro || showComboCheer
            ? "inter"
            : isConcept
              ? "concept"
              : locked
                ? "verdict"
                : "check";
    // Le contenu de l'étape est remonté à chaque bascule : un focus clavier posé
    // dessus retomberait sur <body> (Espace ne ferait plus rien). On repose le
    // focus sur le bouton d'action, sans jamais le voler à un champ.
    const actionBtnRef = useRef(null);
    // Date de la dernière bascule : sert de verrou anti-clic fantôme (ci-dessous).
    const stageAtRef = useRef(0);
    // Dernier appui sur la barre d'action (n'importe lequel de ses boutons :
    // l'événement remonte jusqu'au groupe). Le doigt est noté à part : un
    // double-tap ne se trahit pas comme un double-clic (voir plus bas).
    const pressRef = useRef({ at: 0, touch: false });
    useEffect(() => {
        stageAtRef.current = Date.now();
        const ae = document.activeElement;
        if (ae && ae !== document.body) return;
        const btn = actionBtnRef.current;
        if (btn && !btn.disabled) btn.focus({ preventScroll: true });
    }, [actionStage]);
    // Ce clic visait-il l'état PRÉCÉDENT du groupe d'actions ? Un clic posé
    // après la bascule est délibéré et doit passer, même s'il arrive vite :
    // c'est là-dessus que se joue la sensation de réactivité.
    //
    // Un seul signe est irréfutable, et il vaut dans tous les cas : l'appui a
    // commencé AVANT la bascule. Ce clic-là visait « Vérifier », il ne peut
    // pas viser « Continuer ». (detail > 0 = souris ou doigt ; une activation
    // clavier vaut detail = 0 et n'a aucun appui à comparer.)
    //
    // Le reste ne sert qu'à protéger la CORRECTION d'une erreur : y sauter par
    // réflexe, c'est perdre l'explication. Bonne réponse = rien à rater, et
    // l'apprenant qui reclique aussitôt veut la suite : on le laisse passer,
    // sinon son clic tombe dans le vide et il doit le refaire.
    const reussi = locked && lastCorrect === true;
    const isGhostClick = (e) => {
        if (e && e.detail > 0 && pressRef.current.at < stageAtRef.current) {
            return true;
        }
        if (reussi) return false;
        // 2e coup d'un double-clic : personne ne double-clique pour avancer
        // deux fois.
        if (e && e.detail > 1) return true;
        // Au doigt, un second tap est un clic ordinaire (detail = 1) : rien ne
        // le distingue d'un tap voulu, sauf sa précipitation. C'est le seul cas
        // qui justifie encore une vraie fenêtre aveugle.
        const blind = pressRef.current.touch ? GHOST_TAP_MS : GHOST_CLICK_MS;
        return Date.now() - stageAtRef.current < blind;
    };

    function handleChange(a, complete) {
        setAnswer(a);
        setCanCheck(complete);
    }

    function advance(requeue) {
        const nq = requeue ? [...queue.slice(1), queue[0]] : queue.slice(1);
        if (nq.length === 0) {
            play("next");
            setDone(true);
            return;
        }
        // Debut de la phase revision : la prochaine etape a deja ete vue (erreur requeuee).
        const nextCur = nq[0];
        const enteringReview =
            !reviewStarted && presentedRef.current.has(nextCur);
        if (enteringReview) {
            setReviewStarted(true);
            setReviewMsg(pickCheer(REVIEW_CHEERS));
            setShowReviewIntro(true);
            play("secondChance");
        }
        // Interstitiels BeBot "riches" (BeBot plein cadre + bulle). Deux declencheurs,
        // avec des quotas quotidiens distincts (voir COMBO_CAP_PER_DAY / MID_CAP) :
        //  - COMBO : on vient d'atteindre un palier de bonnes reponses d'affilee
        //    (le moment gratifiant -> prioritaire ; combo de 8 illimite) ;
        //  - MI-PARCOURS : on franchit la moitie d'une lecon assez longue (>= 4),
        //    au plus une fois par lecon ET deux fois par jour.
        const newResolved = total - nq.length;
        const comboTier = COMBO_CHEER_AT.includes(combo) ? combo : null;
        const enteringCombo =
            lastCorrect === true &&
            !reviewStarted &&
            !enteringReview &&
            comboTier != null &&
            canShowCombo(comboTier);
        const enteringMid =
            !enteringCombo &&
            !midShownRef.current &&
            !reviewStarted &&
            !enteringReview &&
            total >= 4 &&
            newResolved >= Math.ceil(total / 2) &&
            canShowMid();
        if (enteringCombo) {
            const c = pickCheer(COMBO_CHEERS);
            setComboMsg({ pose: c.pose, textKey: c.textKey, values: { count: combo } });
            setShowComboCheer(true);
            bumpComboBudget(comboTier);
            play("streak");
        } else if (enteringMid) {
            midShownRef.current = true;
            setMidMsg(pickCheer(MID_CHEERS));
            setShowMidCheer(true);
            bumpMidBudget();
            play("middle");
        }
        // Son de navigation : pas quand on entre dans un interstitiel (il a deja son son).
        if (!enteringReview && !enteringCombo && !enteringMid) {
            play("next");
        }
        presentedRef.current.add(nextCur);
        setQueue(nq);
        setVisit((v) => v + 1);
        setAnswer(null);
        setCanCheck(false);
        setLocked(false);
        setLastCorrect(null);
        setShowExplain(false);
        setHintShown(false);
    }

    function handleAction(e) {
        if (done) return;
        // Navigation libre du mode revue : la barre d'action est remplacée
        // par Précédent/Suivant, Entrée ne valide rien — la file ne doit pas
        // bouger sur un step feuilleté. Les interstitiels BeBot restent
        // fermables (leur bouton Suivant passe par ici).
        if (
            reviewRef.current.browsing &&
            !showReviewIntro &&
            !showMidCheer &&
            !showComboCheer
        )
            return;
        // Le bouton vient de changer de rôle sous le curseur : ce clic-là
        // s'adressait à l'action précédente, on le laisse tomber.
        if (isGhostClick(e)) return;
        // Interstitiels BeBot : la barre du bas (bouton Suivant) les fait avancer.
        if (showReviewIntro) {
            play("next");
            setShowReviewIntro(false);
            return;
        }
        if (showMidCheer) {
            play("next");
            setShowMidCheer(false);
            return;
        }
        if (showComboCheer) {
            play("next");
            setShowComboCheer(false);
            return;
        }
        if (isConcept) {
            advance(false);
            return;
        }
        if (locked) {
            // Légendaire : la 3e vie est tombée → fin de partie (pas de requeue).
            if (isLegendary && !lastCorrect && lives <= 0) {
                setLegendFailed(true);
                return;
            }
            // Une étape qu'on a déjà ratée deux fois ne revient plus : la
            // réponse a été montrée au premier échec, la remettre en file ne
            // ferait qu'enfermer l'apprenant sur la fin de sa leçon.
            //
            // Le mode « insister » lève cette limite — mais seulement là où
            // l'apprenant a de quoi s'en sortir. Sans réponse visible, insister
            // indéfiniment serait une boucle sans issue : il n'a rien à voir,
            // rien à corriger, et sa leçon ne peut plus se terminer.
            const seuilDeSortie = !learnPrefs.insistUntilCorrect
                ? ECHECS_AVANT_DE_PASSER
                : solutionAtteignable
                  ? Infinity
                  : ECHECS_AVANT_DE_PASSER_SANS_ISSUE;
            const dejaRatee = (echecsRef.current[cur] || 0) >= seuilDeSortie;
            advance(!lastCorrect && !dejaRatee);
            return;
        }
        if (!canCheck) return;
        // 1re réponse de cet exercice : mémorisée pour la re-correction serveur.
        // Le verdict affiché reste calculé en local = instantané. (Plus de décompte
        // par question : la clé est déjà dépensée au démarrage de la leçon.)
        if (!(cur in answersRef.current)) {
            answersRef.current[cur] = answer;
        }
        // Étape REJOUÉE : l'arbitrage de la tentative précédente ne juge pas
        // cette réponse-ci. Sans ce reset, l'ancienne raison de BeBot se
        // réaffiche et le bouton de contestation ne revient jamais.
        setAppeals((a) => {
            if (!(cur in a)) return a;
            const next = { ...a };
            delete next[cur];
            return next;
        });
        const ok = gradeExercise(ex, answer);
        // nextFlash calculé ici (et non via le setter fonctionnel) : la voix
        // doit dire LA MÊME phrase que la bulle qui va s'afficher.
        const nextFlash = flashId + 1;
        if (ok) {
            setCombo(combo + 1);
            setFlashId(nextFlash);
            // Réaction COUPLÉE : soit bulle + voix, soit rien du tout.
            const st = okReactRef.current;
            const react =
                !st.firstDone ||
                (!st.lastFired && Math.random() < OK_REACT_P);
            st.firstDone = true;
            st.lastFired = react;
            setHideOkBubble(!react);
            if (react) speakVerdict(true, nextFlash);
            haptic([0, 35, 25, 35]);
        } else {
            setCombo(0);
            setWrongSteps((s) => new Set(s).add(cur));
            echecsRef.current[cur] = (echecsRef.current[cur] || 0) + 1;
            // Légendaire : chaque verdict faux consomme une vie.
            if (isLegendary) setLives((l) => Math.max(0, l - 1));
            // La bulle d'échec s'affiche toujours : la voix l'accompagne à
            // chaque fois (cohérence bulle = voix).
            speakVerdict(false, nextFlash);
            haptic(120);
        }
        play(ok ? "success" : "error");
        setLastCorrect(ok);
        setLocked(true);
    }

    // Arbitrage BeBot : l'élève conteste un échec sur un exercice de code.
    // JAMAIS d'IA en correction systématique : une vérification unique, à sa
    // demande. Verdict favorable = l'étape passe en réussie (pas de re-file) ;
    // le serveur signale automatiquement la règle trop stricte à l'équipe.
    async function requestAppeal() {
        const state = appeals[cur];
        if (state && state.status !== "error") return;
        setAppeals((a) => ({ ...a, [cur]: { status: "loading" } }));
        // L'arbitrage se vit DANS le chat : on l'ouvre tout de suite, BeBot
        // « écrit » (pointillés), et sa réponse s'affiche dedans pour qu'on
        // puisse y réagir et poser des questions dans la foulée.
        setChatOpen(true);
        assistantRef.current?.beginAssistantMessage();
        try {
            const res = await fetch("/api/reps/appeal", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    slug: lessonKey,
                    position: cur,
                    contentLocale,
                    answer,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || "");
            }
            if (data.verdict === "correct") {
                setAppeals((a) => ({
                    ...a,
                    [cur]: { status: "granted", reason: data.reason },
                }));
                setLastCorrect(true);
                // Nouvelle clé/cheer pour la bulle « ok » (et le faisceau de la
                // barre rejoue) : sinon on resservirait mot pour mot le
                // compliment de la dernière bonne réponse. Arbitrage gagné =
                // toujours célébré, bulle ET voix (cohérence bulle = voix).
                const nextFlash = flashId + 1;
                setFlashId(nextFlash);
                setHideOkBubble(false);
                okReactRef.current.firstDone = true;
                okReactRef.current.lastFired = true;
                speakVerdict(true, nextFlash);
                setWrongSteps((s) => {
                    const next = new Set(s);
                    next.delete(cur);
                    return next;
                });
                // Légendaire : l'arbitrage gagné rend la vie consommée par le
                // verdict initial (la réponse était bonne).
                if (isLegendary) {
                    setLives((l) => Math.min(LEGENDARY_LIVES, l + 1));
                }
                play("success");
            } else {
                setAppeals((a) => ({
                    ...a,
                    [cur]: { status: "denied", reason: data.reason },
                }));
            }
            // La réponse d'arbitrage s'écrit dans le chat (raison BeBot, déjà
            // tutoyée et bienveillante) ; replis i18n à la PREMIÈRE personne
            // (dans le chat, c'est BeBot qui parle, jamais la voix plateforme).
            assistantRef.current?.completeAssistantMessage(
                data.reason ||
                    (data.verdict === "correct"
                        ? t("reps_appeal_granted_chat")
                        : t("reps_appeal_denied_chat"))
            );
        } catch (e) {
            setAppeals((a) => ({ ...a, [cur]: { status: "error" } }));
            assistantRef.current?.completeAssistantMessage(
                t("reps_assistant_error")
            );
        }
    }

    const actionRef = useRef(handleAction);
    actionRef.current = handleAction;
    // Écrans légendaires (intro / échec) affichés : le player est caché
    // derrière — le clavier global ne doit NI le piloter NI neutraliser
    // l'activation native des boutons de l'écran (Tab + Entrée).
    const legendGateRef = useRef(false);
    legendGateRef.current = isLegendary && (!legendStarted || legendFailed);
    useEffect(() => {
        function onKey(e) {
            if (legendGateRef.current) return;
            // Focus dans le chat BeBot : le player ne capte rien (Entrée = envoyer).
            const el = e.target;
            if (el && el.closest && el.closest("[data-bebot-chat]")) return;
            if (e.key === "Escape") {
                setShowExplain(false);
                setShowQuit(false);
                setShowReport(false);
                setShowReviewKo(false);
                return;
            }
            const mod = e.metaKey || e.ctrlKey;
            if (mod && e.key.toLowerCase() === "i") {
                if (showExplain) {
                    e.preventDefault();
                    setShowExplain(false);
                } else if (
                    hasExplain &&
                    !showQuit &&
                    !showReviewIntro &&
                    !showReport &&
                    !showMidCheer &&
                    !showComboCheer
                ) {
                    e.preventDefault();
                    setShowExplain(true);
                }
                return;
            }
            // Pendant un interstitiel BeBot, Entree = "Suivant" (gere par handleAction).
            if (showExplain || showQuit || showReport || showReviewKo) return;
            // MODE REVUE : V = étape validée, X = invalider (ouvre les motifs).
            // Jamais pendant une saisie — un « v » tapé dans une réponse reste
            // un « v ».
            if (reviewRef.current.active && !mod && !e.altKey) {
                const k = e.key.toLowerCase();
                if (k === "v" || k === "x") {
                    const ae =
                        typeof document !== "undefined"
                            ? document.activeElement
                            : null;
                    const typing =
                        ae &&
                        (ae.tagName === "TEXTAREA" ||
                            ae.tagName === "INPUT" ||
                            ae.isContentEditable ||
                            (ae.closest && ae.closest(".monaco-editor")));
                    if (!typing) {
                        e.preventDefault();
                        if (e.repeat) return;
                        if (k === "v") reviewRef.current.ok();
                        else reviewRef.current.ko();
                        return;
                    }
                }
            }
            // NAVIGATION LIBRE : les flèches feuillettent les steps. Jamais
            // pendant une saisie (un curseur input/range se pilote aux
            // flèches).
            if (
                reviewRef.current.browsing &&
                (e.key === "ArrowRight" || e.key === "ArrowLeft")
            ) {
                const ae =
                    typeof document !== "undefined"
                        ? document.activeElement
                        : null;
                const typing =
                    ae &&
                    (ae.tagName === "TEXTAREA" ||
                        ae.tagName === "INPUT" ||
                        ae.isContentEditable ||
                        (ae.closest && ae.closest(".monaco-editor")));
                if (!typing) {
                    e.preventDefault();
                    if (e.key === "ArrowRight") reviewRef.current.next();
                    else reviewRef.current.prev();
                    return;
                }
            }
            if (e.key === "Enter") {
                // Si on tape dans un editeur (Monaco) ou un textarea, Entree = retour
                // a la ligne, pas validation. Monaco retargette l'event, donc on se
                // base sur l'element reellement focus.
                const ae =
                    typeof document !== "undefined"
                        ? document.activeElement
                        : null;
                const inEditor =
                    ae &&
                    (ae.tagName === "TEXTAREA" ||
                        (ae.closest && ae.closest(".monaco-editor")));
                if (inEditor && !mod) return;
                e.preventDefault();
                // Entrée MAINTENUE : le système répète la touche. Seule la
                // première pression compte, sinon la leçon défile toute seule.
                if (e.repeat) return;
                actionRef.current();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [
        showExplain,
        showQuit,
        showReviewIntro,
        showReport,
        showReviewKo,
        showMidCheer,
        showComboCheer,
        hasExplain,
    ]);

    function restart() {
        setQueue(steps.map((_, i) => i));
        setVisit(0);
        setCombo(0);
        setWrongSteps(new Set());
        setAnswer(null);
        setCanCheck(false);
        setLocked(false);
        setLastCorrect(null);
        // Chaque tentative repart avec sa cadence de réactions BeBot à zéro
        // (la 1re bonne réponse re-déclenchera bulle + voix).
        setHideOkBubble(false);
        okReactRef.current = { firstDone: false, lastFired: false };
        setDone(false);
        setBurst(false);
        setShowStreak(false);
        // Sans ce retour à zéro, un submit refusé lors d'une première tentative
        // priverait de célébration la partie suivante, pourtant créditée.
        setSubmitFailed(false);
        setShowExplain(false);
        setHintShown(false);
        setShowQuit(false);
        setShowReviewIntro(false);
        setReviewStarted(false);
        setShowMidCheer(false);
        midShownRef.current = false;
        setShowComboCheer(false);
        setShowReport(false);
        // Chaque tentative repart avec ses contestations BeBot à zéro.
        setAppeals({});
        presentedRef.current = new Set([0]);
        answersRef.current = {};
        submittedRef.current = false;
        // La séquence de fin repart à zéro : la tentative suivante a droit à
        // son propre écran de quêtes (l'instantané d'avant, lui, reste celui
        // du montage — c'est bien la progression du jour qu'on raconte).
        setQuestsCelebration(null);
        setBadgeCelebration(null);
        questsUpdateRef.current = null;
        questsPartRef.current = false;
        submitPipelineRef.current = null;
        // Réévalué à la fin de la tentative suivante : la journée vient d'être
        // marquée, donc ce ne sera plus la première leçon du jour et la série
        // ne sera pas re-célébrée.
        firstOfDayRef.current = null;
        // Légendaire : chaque tentative repart avec ses 3 vies (le Recommencer
        // de l'écran de fin passe aussi par ici), et une réussite fraîche fait
        // basculer la promesse d'XP au tarif révision.
        setLives(LEGENDARY_LIVES);
        if (isLegendary) setWasLegendary(isLegendaryDone(lessonKey));
    }

    // Sans compte : la clé se dépense À L'OUVERTURE, côté serveur, sur le
    // cookie httpOnly que la page ne peut pas effacer. Le marqueur localStorage
    // d'avant se vidait depuis la console, donc ne bridait que les honnêtes.
    // Plus de clé = mur d'inscription à la place du player, exactement comme
    // l'écran « plus de clés » d'un membre.
    useEffect(() => {
        if (!isAnon || guestStartRef.current) return;
        guestStartRef.current = true;
        let annule = false;
        (async () => {
            try {
                const res = await fetch("/api/reps/guest-start", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        timezone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone ||
                            "Europe/Paris",
                    }),
                });
                if (!res.ok) return; // panne : on laisse jouer, jamais de mur par erreur
                const data = await res.json();
                if (!annule && data?.blocked) setTrialUsed(true);
            } catch (e) {
                /* réseau : on laisse jouer plutôt que de bloquer à tort */
            }
        })();
        return () => {
            annule = true;
        };
    }, [isAnon]);

    // Défi légendaire réussi : le nœud OR du path est marqué localement
    // (parité mobile — décoration navigateur, le serveur garde sa vérité XP).
    useEffect(() => {
        if (done && isLegendary) markLegendaryDone(lessonKey);
    }, [done, isLegendary, lessonKey]);

    // Visiteur sans compte ayant déjà terminé sa leçon découverte : mur
    // d'inscription plutôt qu'un rejeu illimité. `!done` protège l'écran de fin
    // de la leçon en cours (où `trialUsed` vient tout juste d'être posé).
    if (isAnon && trialUsed && !done) {
        return <RepsSignupGate loginRedirect={loginRedirect} />;
    }

    // Défi légendaire : intro sombre AVANT de jouer (la clé n'est dépensée
    // qu'au COMMENCER), puis écran d'échec quand les 3 vies sont tombées.
    if (isLegendary && !legendStarted) {
        return (
            <LegendaryGate
                hero={
                    <SolarIcon
                        name="cup-star-bold"
                        className="text-8xl"
                        style={{ color: LEGENDARY_COLORS.base }}
                    />
                }
                subtitle={t("reps_legendary_intro_sub")}
                cta={t("reps_legendary_intro_cta", {
                    xp: wasLegendary ? LEGENDARY_REVIEW_XP : LEGENDARY_XP,
                })}
                onCta={() => {
                    // Le jingle de démarrage part via l'effet dédié dès que
                    // legendStarted passe à vrai (une seule fois).
                    setLegendStarted(true);
                }}
                link={t("reps_legendary_later")}
                linkHref={backHref}
            />
        );
    }
    if (isLegendary && legendFailed) {
        return (
            <LegendaryGate
                hero={
                    <Image
                        src="/images/bebot/bebot-sad-1k.png"
                        alt="BeBot"
                        width={110}
                        height={110}
                        className="h-28 w-auto"
                    />
                }
                title={t("reps_legendary_fail_title")}
                subtitle={t("reps_legendary_fail_sub")}
                cta={t("reps_legendary_retry")}
                onCta={() => {
                    restart();
                    setLegendFailed(false);
                }}
                link={t("reps_quit_confirm")}
                linkHref={backHref}
            />
        );
    }

    // Plus de clé au démarrage -> on bloque ici (jamais en cours de leçon).
    if (keysBlocked) {
        return (
            <RepsOutOfKeys
                keys={keysState}
                onBought={async () => {
                    // On a racheté une clé : on relance le start (qui la dépense)
                    // pour pouvoir jouer cette leçon.
                    try {
                        const res = await fetch("/api/reps/start", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                slug: lessonKey,
                                contentKey: lessonContentKey,
                            }),
                        });
                        const data = await res.json();
                        if (data.blocked) {
                            setKeysState(data.keys || null);
                            return;
                        }
                        if (data.keys) setKeysState(data.keys);
                        startOkRef.current = true;
                        setKeysBlocked(false);
                    } catch (e) {
                        setKeysBlocked(false);
                    }
                }}
            />
        );
    }

    // Séquence de fin, dans l'ordre de l'app mobile : récap -> série (1re du
    // jour) -> quêtes qui ont avancé -> badge du mois s'il vient d'être
    // décroché -> retour au parcours.
    if (done && badgeCelebration) {
        return (
            <RepsBadgeCelebration
                badge={badgeCelebration}
                onContinue={leaveToPath}
                leaving={leaving}
                onSound={play}
            />
        );
    }
    if (done && questsCelebration) {
        return (
            <RepsQuestsCelebration
                update={questsCelebration}
                onContinue={showBadgeOrLeave}
                leaving={leaving}
                onSound={play}
            />
        );
    }
    if (done && showStreak) {
        return (
            <StreakCelebration
                streak={streakShown}
                onContinue={showQuestsOrLeave}
                leaving={leaving}
            />
        );
    }
    if (done) {
        const firstTry = Math.max(0, practiceTotal - wrongSteps.size);
        const perfect = practiceTotal > 0 && wrongSteps.size === 0;
        const accuracy =
            practiceTotal > 0
                ? Math.round((firstTry / practiceTotal) * 100)
                : 100;
        // Célébration de série : le local tranche (première leçon du jour sur
        // ce navigateur), le serveur confirme quand sa réponse est là. Un
        // streakGrew explicitement faux l'emporte : la série avait déjà été
        // comptée ailleurs (autre appareil, formation, accélérateur).
        const celebrateStreak =
            !isAnon &&
            firstOfDayRef.current === true &&
            serverResult?.streakGrew !== false &&
            // Submit refusé = rien de crédité : fêter une série ici, et pire,
            // afficher un chiffre déduit de la session +1, annonçait un jour
            // que le serveur n'a jamais compté. Une réponse simplement en
            // route (serverResult null, submitFailed faux) reste célébrée.
            !submitFailed;
        return (
            // Le récap défile quand il déborde (petit écran, texte agrandi,
            // barre d'objectif du jour en plus) : en overflow-hidden, le
            // bouton Continuer devenait inatteignable. justify-center reste
            // vrai tant que ça tient (grâce à min-h-full sur l'enfant).
            <div className="fixed inset-0 z-[60] overflow-y-auto bg-[#f5f5fa] text-gray-900 dark:bg-[#192734] dark:text-white">
                {burst && !reduce && (
                    <div className="pointer-events-none fixed left-1/2 top-1/3 -translate-x-1/2">
                        <ConfettiExplosion
                            force={0.8}
                            duration={2800}
                            particleCount={perfect ? 200 : 140}
                            width={1600}
                            colors={[
                                "#f97316",
                                "#8b5cf6",
                                "#10b981",
                                "#38bdf8",
                                "#f59e0b",
                                "#ec4899",
                            ]}
                        />
                    </div>
                )}
                {/* Plus de zoom d'ensemble (l'écran naissait tout petit puis
                    s'élargissait) : chaque élément arrive pour lui-même, en
                    fondu et légère montée. */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="relative flex min-h-full flex-col items-center justify-center px-6 py-8 text-center"
                >
                    {perfect && !reduce && (
                        <motion.div
                            aria-hidden
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="absolute -top-6 h-40 w-40 rounded-full bg-amber-300/35 blur-3xl"
                        />
                    )}
                    <motion.div
                        initial={
                            reduce
                                ? { opacity: 0 }
                                : { opacity: 0, y: 12, scale: 0.96 }
                        }
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.34, ease: "easeOut" }}
                        className="relative"
                    >
                        <BebotMascot width="170px" />
                    </motion.div>
                    <motion.h2
                        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.28, ease: "easeOut", delay: 0.08 }}
                        className={`mt-2 text-[28px] font-extrabold tracking-tight ${
                            perfect ? "text-amber-500" : ""
                        }`}
                    >
                        {perfect ? t("reps_end_perfect_title") : t("reps_end_title")}
                    </motion.h2>
                    <motion.p
                        initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.28, ease: "easeOut", delay: 0.14 }}
                        className="mt-2.5 max-w-sm text-[15px] leading-relaxed text-gray-500 dark:text-grayDark-400"
                    >
                        {isAnon
                            ? t("reps_signup_gate_end_message")
                            : perfect
                            ? t("reps_end_perfect_subtitle")
                            : t("reps_end_subtitle")}
                    </motion.p>

                    {/* Récapitulatif : lignes libellé/chiffre, puis l'objectif
                        du jour EN DERNIER BLOC de la même carte. Tout apparaît
                        en cascade (fondu + 8 px, sobre), la barre se remplit
                        ensuite. */}
                    <motion.div
                        initial="hidden"
                        animate="show"
                        variants={{
                            hidden: {},
                            show: {
                                transition: {
                                    staggerChildren: 0.08,
                                    delayChildren: 0.15,
                                },
                            },
                        }}
                        className="mt-9 w-full max-w-md divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-100 bg-white text-left dark:divide-white/[0.06] dark:border-white/[0.06] dark:bg-white/[0.05]"
                    >
                        {[
                            {
                                l: t("reps_stat_xp_earned"),
                                node: (
                                    <>
                                        +
                                        <CountUp
                                            to={
                                                // Légendaire : XP promis du défi
                                                // (plein tarif puis révision) ;
                                                // révision : tarif révision.
                                                isLegendary
                                                    ? wasLegendary
                                                        ? LEGENDARY_REVIEW_XP
                                                        : LEGENDARY_XP
                                                    : isReview
                                                      ? LEGENDARY_REVIEW_XP
                                                      : lesson.rewardXp || 20
                                            }
                                        />{" "}
                                        XP
                                    </>
                                ),
                            },
                            {
                                l: t("reps_stat_no_mistake"),
                                node: `${firstTry}/${practiceTotal}`,
                            },
                            {
                                l: t("reps_stat_accuracy"),
                                node: <CountUp to={accuracy} suffix="%" />,
                            },
                        ].map((s, i) => (
                            <motion.div
                                key={i}
                                variants={STAT_ROW_VARIANTS}
                                className="flex items-center justify-between px-6 py-5"
                            >
                                <span className="text-sm text-gray-500 dark:text-grayDark-400">
                                    {s.l}
                                </span>
                                <span className="text-[17px] font-semibold tabular-nums text-gray-900 dark:text-white">
                                    {s.node}
                                </span>
                            </motion.div>
                        ))}

                        {/* Objectif du jour : dernier bloc de la carte. Masqué
                            si l'apprenant n'a pas d'objectif. */}
                        {serverResult?.dailyGoal?.goalMinutes ? (
                            <motion.div
                                variants={STAT_ROW_VARIANTS}
                                className="px-6 py-5"
                            >
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <span className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-grayDark-400">
                                        {serverResult.dailyGoal.reached && (
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="text-base text-emerald-500"
                                            />
                                        )}
                                        {serverResult.dailyGoal.reached
                                            ? t("reps_daily_goal_reached")
                                            : t("reps_daily_goal_label")}
                                    </span>
                                    <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                                        {serverResult.dailyGoal.reached
                                            ? t("reps_daily_goal_total", {
                                                  done: serverResult.dailyGoal
                                                      .minutesToday,
                                              })
                                            : t("reps_daily_goal_progress", {
                                                  done: serverResult.dailyGoal
                                                      .minutesToday,
                                                  goal: serverResult.dailyGoal
                                                      .goalMinutes,
                                              })}
                                    </span>
                                </div>
                                <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{
                                            width: `${Math.min(
                                                100,
                                                Math.round(
                                                    (serverResult.dailyGoal
                                                        .minutesToday /
                                                        serverResult.dailyGoal
                                                            .goalMinutes) *
                                                        100
                                                )
                                            )}%`,
                                        }}
                                        transition={{
                                            duration: 0.8,
                                            ease: "easeOut",
                                            delay: 0.5,
                                        }}
                                        className={`h-full rounded-full ${
                                            serverResult.dailyGoal.reached
                                                ? "bg-emerald-500"
                                                : "bg-orange-500"
                                        }`}
                                        style={
                                            // Stries de complétion : même motif
                                            // que les nœuds terminés du chemin.
                                            serverResult.dailyGoal.reached
                                                ? {
                                                      backgroundImage:
                                                          "repeating-linear-gradient(45deg, rgba(255,255,255,0.16) 0px, rgba(255,255,255,0.16) 11px, transparent 11px, transparent 24px)",
                                                  }
                                                : undefined
                                        }
                                    />
                                </div>
                            </motion.div>
                        ) : null}
                    </motion.div>

                    {isAnon ? (
                        // Visiteur sans compte : sa progression (ces XP, ce
                        // sans-faute) n'est pas sauvegardée. On capitalise sur la
                        // satisfaction de fin de leçon pour l'inviter à s'inscrire.
                        <motion.div
                            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.28,
                                ease: "easeOut",
                                delay: 0.55,
                            }}
                            className="mt-9 flex justify-center"
                        >
                            <RepsSignupButtons loginRedirect={loginRedirect} />
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.28,
                                ease: "easeOut",
                                delay: 0.55,
                            }}
                            className="mt-9 flex w-full max-w-md flex-col items-center gap-3"
                        >
                            {/* Un SEUL bouton, pleine largeur : c'est ce que
                                font Duolingo, Brilliant et Mimo. Recommencer a
                                été retiré d'ici — refaire une leçon se décide
                                depuis le chemin, pas dans l'élan de la
                                réussite. */}
                            {celebrateStreak ? (
                                // Première leçon du jour : on célèbre (une
                                // fois/jour). Décidé en local — attendre
                                // streakGrew privait de sa célébration tout
                                // apprenant dont le submit traînait.
                                <RepsContinueButton
                                    onClick={() => {
                                        // Chiffre FIGÉ à l'ouverture : celui du
                                        // serveur s'il est arrivé, sinon la
                                        // série connue + 1 (on n'ouvre cet
                                        // écran que si la journée vient d'être
                                        // comptée). Sans ce gel, une réponse
                                        // arrivant écran ouvert faisait
                                        // retomber le compteur à zéro pour
                                        // remonter sous les yeux de
                                        // l'apprenant. Jamais moins de 1 : un
                                        // compte neuf est à 0 en session.
                                        setStreakShown(
                                            Math.max(
                                                1,
                                                serverResult?.streakCount ??
                                                    (sessionData?.user
                                                        ?.streakCount ?? 0) + 1
                                            )
                                        );
                                        setShowStreak(true);
                                        play("streak");
                                    }}
                                >
                                    {t("reps_continue")}
                                </RepsContinueButton>
                            ) : (
                                // Série déjà comptée aujourd'hui (ou submit en
                                // attente) : pas d'écran de série, mais les
                                // quêtes ont pu avancer -> on passe par la
                                // séquence, qui sort d'elle-même s'il n'y a
                                // rien à montrer.
                                <RepsContinueButton
                                    onClick={showQuestsOrLeave}
                                    loading={leaving}
                                >
                                    {t("reps_continue")}
                                </RepsContinueButton>
                            )}
                        </motion.div>
                    )}
                </motion.div>
            </div>
        );
    }

    const filled = resolvedCount + (locked && lastCorrect ? 1 : 0);
    const pct = total > 0 ? (filled / total) * 100 : 0;
    // Les paliers sont calibres sur la longueur de la lecon (sans-faute = max).
    const chargeMax = Math.max(practiceTotal, 4);
    const chargeMid = Math.ceil(chargeMax / 2);
    // L'eclair de serie est colore tant que le combo tient (2 d'affilee) et
    // passe en pastille pleine des que la serie est CHAUDE.
    const charged = combo >= COMBO_CHARGE;
    const comboHot = combo >= COMBO_HOT;
    // Paliers majeurs : milieu de lecon et sans-faute complet.
    const milestoneHit =
        charged && (combo === chargeMid || combo >= chargeMax);
    const continueMode = isConcept || locked;
    const willFinish =
        (isConcept || (locked && lastCorrect)) && queue.length <= 1;
    const actionLabel = !continueMode
        ? t("reps_verify")
        : willFinish
          ? t("reps_finish")
          : t("reps_continue");

    let btnCls;
    if (continueMode) {
        if (isConcept) {
            btnCls = "bg-[#8b7ff0] border-b-[#6d5fd0] text-white hover:bg-[#7c6fe8]";
        } else if (lastCorrect) {
            btnCls = "bg-emerald-500 border-b-emerald-700 text-white hover:bg-emerald-600";
        } else {
            btnCls = "bg-red-500 border-b-red-700 text-white hover:bg-red-600";
        }
    } else if (canCheck) {
        btnCls = "bg-orange-500 border-b-orange-700 text-white hover:bg-orange-600";
    } else {
        btnCls =
            "cursor-not-allowed bg-gray-200 text-gray-400 border-b-gray-300 dark:bg-white/10 dark:text-grayDark-500 dark:border-b-white/5";
    }

    // Façon Brilliant : au verdict, c'est TOUT l'encadré de la leçon qui s'anime
    // (bordure + halo doux qui montent en fondu via la transition CSS de la
    // carte), pas seulement la barre du bas. Vert = juste, rouge = raté.
    // Le halo remplace le shadow-sm de repos (classes mutuellement exclusives).
    const cardVerdictCls = locked
        ? lastCorrect
            ? "sm:border-emerald-400 sm:shadow-[0_0_0_1px_#34d399,0_0_28px_-6px_rgba(16,185,129,0.45)] sm:dark:border-emerald-400/70 sm:dark:shadow-[0_0_0_1px_rgba(52,211,153,0.55),0_0_28px_-6px_rgba(16,185,129,0.35)]"
            : "sm:border-red-300 sm:shadow-[0_0_0_1px_#fca5a5,0_0_28px_-6px_rgba(239,68,68,0.35)] sm:dark:border-red-400/60 sm:dark:shadow-[0_0_0_1px_rgba(248,113,113,0.5),0_0_28px_-6px_rgba(239,68,68,0.3)]"
        : "sm:border-gray-200 sm:shadow-sm sm:dark:border-white/10";

    // Interstitiel BeBot (mi-parcours / entree en revision) : on garde la barre du
    // haut ET la barre du bas (bouton Suivant), seul le contenu central change.
    // `interstitial` est declare plus haut, avec la mesure de debordement.
    const interMsg = showReviewIntro
        ? reviewMsg
        : showComboCheer
          ? comboMsg
          : midMsg;
    const interLabel =
        showReviewIntro || showComboCheer
            ? t("reps_lets_go")
            : t("reps_continue");

    // BeBot PARLE (façon Brilliant) : verdict et commentaires vivent dans une
    // bulle en bas à gauche, plus jamais dans la barre. Rien pendant un
    // interstitiel (cur pointe déjà l'étape suivante) ni chat ouvert (BeBot y
    // parle déjà). La clé fait remonter la bulle à chaque nouveau message.
    // Indice disponible : une pratique non encore répondue qui porte data.hint.
    // JAMAIS en défi légendaire : y jouer sans filet est tout l'intérêt du mode
    // (comme l'explication d'après-verdict, déjà retirée là-bas).
    const stepHint = !isConcept && ex && ex.data ? ex.data.hint : null;
    const canAskHint =
        Boolean(stepHint) && !locked && !interstitial && !isLegendary;

    const bubble = interstitial
        ? null
        : locked && lastCorrect
          ? hideOkBubble
              ? null
              : { kind: "ok", key: `ok-${flashId}` }
          : locked
            ? { kind: "ko", key: `ko-${cur}` }
            : hintShown && stepHint
              ? { kind: "hint", key: `hint-${cur}` }
              : isConcept && ex.data && ex.data.botSay
                ? { kind: "info", key: `info-${cur}` }
                : null;

    return (
        // Fond BLANC (façon Brilliant) : la leçon vit dans une carte encadrée qui
        // se détache par sa bordure ; le chat BeBot s'ouvre sur ce même fond.
        <div className="fixed inset-0 z-[60] flex flex-col bg-white text-gray-900 dark:bg-[#192734] dark:text-white">
            {/* Déverrouillage à l'ouverture (clés limitées) : la clé ouvre la leçon */}
            {unlock && (
                <RepsKeyUnlock
                    remaining={unlock.remaining}
                    onDone={() => setUnlock(null)}
                />
            )}
            {/* Barre du haut (meme largeur centree que le pied de page) */}
            <div className="relative z-[55] mx-auto flex w-full max-w-5xl items-center gap-2.5 px-3 py-3 sm:gap-4 sm:px-6 sm:py-3.5">
                <button
                    type="button"
                    onClick={() => setShowQuit(true)}
                    aria-label={t("reps_quit_lesson_aria")}
                    className="text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-white"
                >
                    <SolarIcon name="close-circle-linear" className="text-2xl" />
                </button>
                {/* Barre de progression continue facon Duolingo : un aplat UNI qui
                    ne change jamais de teinte pendant la lecon (violet, ou OR en
                    defi legendaire). L'intensite du combo vit dans l'eclair a
                    droite, plus dans le remplissage. Un faisceau blanc traverse
                    la barre a chaque bonne reponse. */}
                <div className="relative flex flex-1 items-center">
                    <div className="relative h-3.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
                        <motion.div
                            className={`absolute inset-y-0 left-0 overflow-hidden rounded-full ${
                                isLegendary
                                    ? ""
                                    : "bg-violet-500 dark:bg-[#a78bfa]"
                            }`}
                            initial={false}
                            animate={{ width: `${pct}%` }}
                            transition={{
                                type: "spring",
                                stiffness: 180,
                                damping: 26,
                            }}
                            style={
                                // Légendaire : remplissage OR permanent, parité
                                // mobile.
                                isLegendary
                                    ? { backgroundColor: LEGENDARY_COLORS.base }
                                    : undefined
                            }
                        >
                            {/* Reflet superieur (donne le relief des barres Duolingo) */}
                            {pct > 4 && (
                                <span className="absolute inset-x-1.5 top-[3px] h-[3px] rounded-full bg-white/45" />
                            )}
                            {/* Faisceau d'energie qui traverse la barre a chaque bonne reponse */}
                            <AnimatePresence>
                                {locked && lastCorrect && !reduce && (
                                    <motion.span
                                        key={`beam-${flashId}`}
                                        className="absolute inset-y-0 w-10 -skew-x-[20deg] bg-gradient-to-r from-transparent via-white to-transparent"
                                        initial={{ left: "-25%", opacity: 0 }}
                                        animate={{
                                            left: "125%",
                                            opacity: [0, 1, 1, 0],
                                        }}
                                        transition={{
                                            duration: milestoneHit ? 0.6 : 0.45,
                                            ease: "easeOut",
                                            times: [0, 0.15, 0.8, 1],
                                        }}
                                    />
                                )}
                            </AnimatePresence>
                        </motion.div>
                        {/* Lueur qui pulse autour de la barre a chaque bonne reponse */}
                        <AnimatePresence>
                            {locked && lastCorrect && !reduce && (
                                <motion.span
                                    key={`flare-${flashId}`}
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 rounded-full"
                                    initial={{ opacity: 0, scale: 1 }}
                                    animate={{
                                        opacity: [0, 1, 0],
                                        scale: milestoneHit ? [1, 1.3, 1] : 1,
                                    }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: milestoneHit ? 0.7 : 0.5,
                                        ease: "easeOut",
                                    }}
                                    style={{
                                        // Une seule lueur, de la couleur de la
                                        // barre : elle marque la bonne reponse,
                                        // pas le niveau de combo.
                                        boxShadow: isLegendary
                                            ? "0 0 14px 2px rgba(245,201,66,0.9)"
                                            : "0 0 14px 2px rgba(167,139,250,0.85)",
                                    }}
                                />
                            )}
                        </AnimatePresence>
                    </div>
                </div>
                {/* Serie en cours : TOUTE l'intensite du combo vit ici (facon
                    Duolingo et Brilliant), plus dans le remplissage de la barre.
                    Eteint sous 2 bonnes reponses d'affilee. Largeur FIGEE : le
                    nombre qui apparait ne doit jamais decaler la barre. Masque en
                    defi legendaire, ou les 3 vies tiennent deja ce role. */}
                {!isLegendary && (
                    // Reserve de largeur FIGEE : la pastille est plus large que
                    // l'etat plat, et la barre ne doit pas bouger d'un pixel au
                    // passage de l'un a l'autre.
                    <div className="flex w-14 shrink-0 justify-center">
                        <motion.div
                            animate={boltControls}
                            className={`flex items-center gap-0.5 rounded-full text-sm font-bold tabular-nums transition-[color,background-color,padding] duration-300 ${
                                comboHot
                                    ? "bg-orange-500 px-2 py-0.5 text-white"
                                    : boltTone(combo)
                            }`}
                        >
                            <motion.span
                                key={combo}
                                initial={
                                    reduce || !charged
                                        ? false
                                        : {
                                              // Franchir le seuil chaud claque
                                              // plus fort que les crans avant.
                                              scale:
                                                  combo === COMBO_HOT
                                                      ? 1.4
                                                      : 1.25,
                                          }
                                }
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                                className="inline-flex"
                            >
                                <SolarIcon name="bolt-bold" className="text-xl" />
                            </motion.span>
                            <span aria-hidden>{combo}</span>
                            {charged && (
                                <span className="sr-only">
                                    {t("reps_combo_streak", { combo })}
                                </span>
                            )}
                        </motion.div>
                    </div>
                )}
                {/* Vies du défi légendaire : 3 étoiles or, grisées une à une. */}
                {isLegendary ? (
                    <div className="flex items-center gap-0.5">
                        {[0, 1, 2].map((i) => (
                            <SolarIcon
                                key={i}
                                name="star-bold"
                                className={`text-lg ${
                                    i < lives
                                        ? "text-[#F5C942]"
                                        : "text-gray-300 dark:text-white/20"
                                }`}
                            />
                        ))}
                    </div>
                ) : null}
                {/* Chat BeBot sur MOBILE (desktop = dock persistant) : lui aussi
                    remonté avec les outils. */}
                {!chatOpen && !interstitial && (
                    <RepsAssistantLauncher onOpen={() => setChatOpen(true)} />
                )}
                <RepsLearningSettings
                    prefs={learnPrefs}
                    onChange={updateLearnPrefs}
                />
                <SoundSettingsPopover />
                {/* MODE REVUE (admin) : V valide l'étape courante, X l'invalide
                    avec un motif. Raccourcis clavier V / X hors saisie. Texte
                    en français en dur : outillage d'administration. */}
                {isAdminReviewer && reviewMode && !done && ex ? (
                    <div className="flex shrink-0 items-center gap-0.5 rounded-full border border-gray-200 py-0.5 pl-2.5 pr-1 dark:border-grayDark-700">
                        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            Revue
                        </span>
                        <button
                            type="button"
                            onClick={() => sendReviewVerdict("ok")}
                            aria-label="Valider cette étape (touche V)"
                            title="Valider cette étape (touche V)"
                            className={`flex shrink-0 items-center gap-1 transition-colors ${
                                reviewVerdicts[cur] === "ok"
                                    ? "text-emerald-500"
                                    : "text-gray-400 hover:text-emerald-500"
                            }`}
                        >
                            <SolarIcon
                                name="check-circle-linear"
                                className="text-xl"
                            />
                            <kbd className="hidden rounded border border-gray-300 px-1 py-0.5 text-[9px] font-semibold leading-none dark:border-grayDark-600 sm:inline">
                                V
                            </kbd>
                        </button>
                        <button
                            type="button"
                            onClick={openReviewKo}
                            aria-label="Invalider cette étape (touche X)"
                            title="Invalider cette étape (touche X)"
                            className={`flex shrink-0 items-center gap-1 transition-colors ${
                                reviewVerdicts[cur] === "ko"
                                    ? "text-rose-500"
                                    : "text-gray-400 hover:text-rose-500"
                            }`}
                        >
                            <SolarIcon
                                name="close-circle-linear"
                                className="text-xl"
                            />
                            <kbd className="hidden rounded border border-gray-300 px-1 py-0.5 text-[9px] font-semibold leading-none dark:border-grayDark-600 sm:inline">
                                X
                            </kbd>
                        </button>
                        <label
                            className="ml-1 flex cursor-pointer select-none items-center gap-1.5 border-l border-gray-200 pl-2 text-[11px] font-medium text-gray-400 dark:border-grayDark-700"
                            title="Feuilleter les étapes sans répondre (flèches ← →)"
                        >
                            <input
                                type="checkbox"
                                checked={reviewBrowse !== null}
                                onChange={toggleReviewBrowse}
                                className="h-3 w-3 accent-blue-500"
                            />
                            Feuilleter
                        </label>
                        {reviewBrowse !== null && (
                            <label
                                className="flex cursor-pointer select-none items-center gap-1.5 pl-1 text-[11px] font-medium text-gray-400"
                                title="Afficher la bonne réponse du step feuilleté"
                            >
                                <input
                                    type="checkbox"
                                    checked={reviewShowAnswer}
                                    onChange={() =>
                                        setReviewShowAnswer((v) => !v)
                                    }
                                    className="h-3 w-3 accent-amber-500"
                                />
                                Solution
                            </label>
                        )}
                    </div>
                ) : null}
                {isAdminReviewer ? (
                    <button
                        type="button"
                        onClick={toggleReviewMode}
                        aria-label={
                            reviewMode
                                ? "Quitter le mode revue"
                                : "Activer le mode revue"
                        }
                        title={
                            reviewMode
                                ? "Quitter le mode revue"
                                : "Mode revue : V valide, X invalide"
                        }
                        className={`shrink-0 transition-colors ${
                            reviewMode
                                ? "text-blue-500 hover:text-blue-600"
                                : "text-gray-400 hover:text-gray-700 dark:hover:text-white"
                        }`}
                    >
                        <SolarIcon
                            name="clipboard-check-linear"
                            className="text-xl"
                        />
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => setShowReport(true)}
                    aria-label={t("report_problem_label")}
                    className="shrink-0 text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-white"
                >
                    <SolarIcon name="flag-linear" className="text-xl" />
                </button>
            </div>

            {/* Facon Brilliant : la lecon vit en PERMANENCE dans une carte encadree.
                Le chat s'ouvre a gauche (desktop) ou en bottom sheet (mobile). Le
                panneau reste MONTE en permanence -> fermer/rouvrir NE PERD PAS la
                conversation ; seule sa visibilite s'anime. */}
            <div className="relative flex min-h-0 flex-1">
            <RepsAssistantPanel
                ref={assistantRef}
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                getContext={() => {
                    const d = (ex && ex.data) || {};
                    // Une fois le verdict tombé, BeBot doit savoir ce que
                    // l'apprenant a RÉELLEMENT répondu (sinon il le lui
                    // redemande) ET ce qui était attendu. Pour le code, les
                    // motifs de correction ne sont plus affichés à l'écran :
                    // BeBot est le SEUL à les recevoir, et il les traduit en
                    // phrase au lieu de les recracher tels quels.
                    // `locked` seul ne suffisait pas : en DEUXIÈME CHANCE
                    // l'éditeur est déverrouillé, donc BeBot ne recevait rien
                    // et redemandait à l'apprenant de lui montrer son code —
                    // juste après le lui avoir refusé. Dès qu'une réponse
                    // existe, il la voit.
                    const answered = locked || answer != null ? describeAnswers() : null;
                    return {
                        lessonTitle: lesson.title,
                        stepType: ex && ex.type,
                        stepPrompt: (ex && ex.prompt) || d.body || "",
                        stepBody: [
                            d.body,
                            d.code,
                            d.template,
                            Array.isArray(d.options)
                                ? "Options : " + d.options.join(" | ")
                                : null,
                        ]
                            .filter(Boolean)
                            .join("\n")
                            .slice(0, 2000),
                        revealed: Boolean(locked),
                        wasCorrect: Boolean(lastCorrect),
                        explanation:
                            locked && ex && ex.explanation
                                ? ex.explanation
                                : undefined,
                        userAnswer:
                            answered && answered.userAnswer
                                ? String(answered.userAnswer).slice(0, 1500)
                                : undefined,
                        expectedAnswer:
                            answered && answered.expectedAnswer
                                ? String(answered.expectedAnswer).slice(0, 800)
                                : undefined,
                        // Verdict d'arbitrage déjà rendu : BeBot s'en souvient
                        // même si l'historique du chat ne le porte pas.
                        appealReason: appeals[cur]?.reason
                            ? String(appeals[cur].reason).slice(0, 1200)
                            : undefined,
                        // Images d'illustration de l'étape (jusqu'à deux) :
                        // BeBot a la vision, il voit ce que l'apprenant voit.
                        image:
                            typeof d.image === "string" ? d.image : undefined,
                        imageAlt:
                            typeof d.imageAlt === "string"
                                ? d.imageAlt
                                : undefined,
                        image2:
                            typeof d.image2 === "string"
                                ? d.image2
                                : undefined,
                        image2Alt:
                            typeof d.image2Alt === "string"
                                ? d.image2Alt
                                : undefined,
                    };
                }}
            />
            <AnimatePresence>
                {chatOpen && (
                    <RepsAssistantHandle
                        key="bebot-handle"
                        onClose={() => setChatOpen(false)}
                    />
                )}
            </AnimatePresence>
            {/* La carte : encadrée en permanence (desktop), le contenu du player
                vit dedans. Marge gauche retirée quand le chat est ouvert (c'est
                lui qui fait l'espace). */}
            <div
                className={`flex min-h-0 min-w-0 flex-1 flex-col transition-[margin,border-color,box-shadow] duration-300 ease-out sm:mb-3 sm:mr-3 sm:overflow-hidden sm:rounded-2xl sm:border sm:bg-white sm:dark:bg-[#22303C] ${
                    chatOpen ? "" : "sm:ml-3"
                } ${cardVerdictCls}`}
            >
            {/* Zone etape : ancienne en fondu, nouvelle qui glisse de la droite par-dessus */}
            <div className="relative flex-1 overflow-hidden">
                {/* La serie ne s'affiche plus ICI : elle vit dans l'eclair de la
                    barre du haut (un seul endroit, facon Duolingo/Brilliant). */}
                {interstitial ? (
                    <div
                        ref={attachScroller}
                        className="absolute inset-0 flex items-center justify-center overflow-y-auto px-6 py-6"
                    >
                        <CheerBody pose={interMsg.pose} message={t(interMsg.textKey, interMsg.values)} />
                    </div>
                ) : (
                <AnimatePresence initial={false}>
                    <motion.div
                        key={visit}
                        initial={reduce ? { opacity: 0 } : { x: "55%", opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            x: { type: "spring", stiffness: 320, damping: 34, mass: 0.9 },
                            opacity: { duration: 0.25 },
                        }}
                        style={{ zIndex: Math.min(visit, 40) }}
                        className="absolute inset-0 overflow-y-auto"
                        ref={attachScroller}
                    >
                        <div className="flex min-h-full items-center justify-center px-4 py-6">
                            <div
                                className={`w-full min-w-0 ${
                                    isWideStep(ex) ? "max-w-6xl" : "max-w-3xl"
                                }`}
                            >
                                {/* Question rejouée (déjà ratée dans cette leçon,
                                    remise en fin de file) : badge « deuxième
                                    chance », distinct du badge « difficile ». */}
                                {/* Les deux badges portent la MÊME enveloppe que la
                                    consigne juste dessous (mx-auto max-w-[600px],
                                    la mesure de lecture) : centrés sur le
                                    conteneur, ils flottaient à 84 px du texte sur
                                    une étape étroite et à 276 px dès qu'il y a du
                                    code, donc alignés sur rien. */}
                                {wrongSteps.has(cur) && !locked ? (
                                    <div className="mx-auto mb-3 flex w-full max-w-[600px] items-center gap-2">
                                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-white">
                                            <SolarIcon
                                                name="restart-bold"
                                                className="text-[13px]"
                                            />
                                        </span>
                                        <span className="text-xs font-extrabold uppercase tracking-wide text-blue-500 dark:text-blue-400">
                                            {t("reps_second_chance")}
                                        </span>
                                    </div>
                                ) : ex.hard ? (
                                    <div className="mx-auto mb-3 flex w-full max-w-[600px] items-center gap-2">
                                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-500 text-white">
                                            <SolarIcon
                                                name="dumbbell-small-bold"
                                                className="text-[13px]"
                                            />
                                        </span>
                                        <span className="text-xs font-extrabold uppercase tracking-wide text-rose-500 dark:text-rose-400">
                                            {t("reps_hard_exercise")}
                                        </span>
                                    </div>
                                ) : null}
                                {ex.prompt && (
                                    <h1 className="mx-auto mb-6 w-full max-w-[600px] whitespace-pre-line text-left text-lg font-medium leading-relaxed text-gray-800 dark:text-grayDark-100">
                                        {/* renderMarkdownInline (pas renderRich) :
                                            les consignes portent du **gras**
                                            comme les concepts (voix METHOD.md).
                                            max-w-[600px] : la mesure de lecture
                                            que le parcours enseigne (45-75
                                            caractères) s'applique à sa propre
                                            prose ; les blocs (éditeur, aperçu,
                                            options) gardent leur largeur. */}
                                        {renderMarkdownInline(ex.prompt)}
                                    </h1>
                                )}
                                <PreviewToggleContext.Provider
                                    value={previewValue}
                                >
                                    <RepsExercise
                                        exercise={ex}
                                        uid={`${lessonKey}-${cur}-${visit}`}
                                        lang={
                                            // Repli si le back n'envoie pas
                                            // `language` : les DEUX préfixes du
                                            // parcours Python (py- historique,
                                            // p2- depuis la refonte). Oublier le
                                            // second a fait afficher tout le
                                            // Python sans coloration.
                                            // JavaScript : même règle,
                                            // préfixe js-.
                                            lesson.language === "javascript" ||
                                            /^js-/.test(lessonKey || "")
                                                ? "javascript"
                                                : lesson.language === "python" ||
                                                    /^(py|p2)-/.test(lessonKey || "")
                                                  ? "python"
                                                  : "html"
                                        }
                                        onChange={handleChange}
                                        onTile={handleTile}
                                        onDrop={handleDrop}
                                        disabled={locked}
                                        revealed={locked}
                                        verdict={locked ? lastCorrect : null}
                                    />
                                </PreviewToggleContext.Provider>
                                {/* Indice, façon Brilliant : un LIEN discret
                                    collé au contenu (comme leur « Start over »),
                                    pas un bouton qui rivalise avec l'action.
                                    Impossible de le toucher par erreur : il est
                                    loin de la barre du bas et n'en a ni la
                                    taille ni le poids. */}
                                {canAskHint && !hintShown && (
                                    <div className="mt-5 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setHintShown(true);
                                                play("click");
                                                speakHint(stepHint);
                                            }}
                                            className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-grayDark-200"
                                        >
                                            <SolarIcon
                                                name="lightbulb-linear"
                                                className="text-base"
                                            />
                                            {t("reps_hint")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
                )}
                {/* L'ombre du débordement : elle dit « ça continue en dessous »
                    et rien d'autre. Douce (dégradé, jamais un trait), jamais
                    cliquable, et z-[45] — au-dessus du contenu de l'étape
                    (z ≤ 40), sous la bulle BeBot (z-[54]) et sous les boutons
                    (z-[55]), qu'elle ne doit ni voiler ni intercepter. Nœud
                    TOUJOURS monté qu'on fait juste fondre : une ombre qui
                    apparaît et disparaît au fil du défilement n'a rien à gagner
                    à être montée puis démontée à chaque fois. */}
                <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-x-0 bottom-0 z-[45] h-9 bg-gradient-to-t from-gray-900/[0.1] via-gray-900/[0.04] to-transparent transition-opacity ease-out dark:from-black/50 dark:via-black/[0.18] ${
                        reduce ? "duration-0" : "duration-300"
                    } ${stepOverflow ? "opacity-100" : "opacity-0"}`}
                />
            </div>

            {/* Bas (façon Brilliant) : PLUS de barre — ni trait ni bandeau
                teinté. Les actions flottent, centrées, en bas de la carte ; le
                verdict est porté par le cadre de la carte et par la bulle
                BeBot (en bas à gauche, c'est LUI qui parle). */}
            <div
                className="relative z-[55]"
                // Horodate l'appui (il remonte de n'importe quel bouton de la
                // barre) : c'est ce qui permet de distinguer un clic parti
                // AVANT la bascule d'un clic délibéré posé juste après.
                onPointerDown={(e) => {
                    pressRef.current = {
                        at: Date.now(),
                        touch: e.pointerType === "touch",
                    };
                }}
            >
                {/* FEUILLETAGE + « Solution » : la bonne réponse du step
                    feuilleté s'affiche dans un panneau ambré au-dessus des
                    flèches — inline, jamais en modale, pour ne pas gêner la
                    navigation. */}
                {reviewBrowsing && reviewShowAnswer && !interstitial && ex ? (
                    <div className="mx-auto w-full max-w-5xl px-3 pt-2 sm:px-6">
                        <div className="rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-2.5 dark:border-amber-500/20 dark:bg-amber-500/10">
                            <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                                <SolarIcon name="key-bold" className="text-sm" />
                                La bonne réponse
                            </div>
                            {ex.type === "concept" ? (
                                <p className="mb-0 text-[13px] italic text-amber-700/80 dark:text-amber-200/80">
                                    Un concept n'attend pas de réponse.
                                </p>
                            ) : ex.type === "find_error" &&
                              ex.solution &&
                              ex.solution.lineIndex != null ? (
                                <p className="mb-0 font-geistMono text-[13px] text-gray-800 dark:text-grayDark-100">
                                    {`Ligne ${ex.solution.lineIndex + 1}`}
                                    {(() => {
                                        const l = String(
                                            (ex.data && ex.data.code) || ""
                                        ).split("\n")[ex.solution.lineIndex];
                                        return l ? ` : ${l.trim()}` : "";
                                    })()}
                                </p>
                            ) : reponseRevueLignes() ? (
                                <div className="space-y-0.5 text-[13px] leading-[1.6] text-gray-800 dark:text-grayDark-100">
                                    {reponseRevueLignes().map((ligne, i) => (
                                        <div key={i}>
                                            {renderMarkdownInline(ligne)}
                                        </div>
                                    ))}
                                </div>
                            ) : reponseMontrable ? (
                                reponseMontrable.kind === "code" ? (
                                    <div className="overflow-hidden rounded-xl bg-[#1e1e1e]">
                                        <div className="max-h-40 overflow-auto px-3 py-2">
                                            {tokenizeLines(
                                                reponseMontrable.value,
                                                reponseMontrable.lang || "html"
                                            ).map((ligne, i) => (
                                                <div
                                                    key={i}
                                                    className="whitespace-pre font-geistMono text-[12px] leading-[1.7] text-[#d4d4d4]"
                                                >
                                                    {ligne.length
                                                        ? ligne.map((tk, j) =>
                                                              tk.kind ? (
                                                                  <span
                                                                      key={j}
                                                                      style={{
                                                                          color: TOKEN_DARK[tk.kind],
                                                                      }}
                                                                  >
                                                                      {tk.text}
                                                                  </span>
                                                              ) : (
                                                                  <span key={j}>
                                                                      {tk.text}
                                                                  </span>
                                                              )
                                                          )
                                                        : " "}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-[13px] leading-[1.6] text-gray-800 dark:text-grayDark-100">
                                        {renderMarkdownInline(
                                            reponseMontrable.value
                                        )}
                                    </div>
                                )
                            ) : (
                                <p className="mb-0 text-[13px] italic text-amber-700/80 dark:text-amber-200/80">
                                    Pas d'affichage simple pour ce type : la
                                    solution vit dans le fichier de la leçon.
                                </p>
                            )}
                        </div>
                    </div>
                ) : null}
                {/* Grille 3 colonnes à TOUTES les tailles (le texte de verdict
                    a quitté la barre). Règle clé : le bouton d'action vit SEUL
                    dans la colonne du milieu et n'est JAMAIS remonté. Il garde
                    donc sa place au pixel près quand « Vérifier » devient
                    « Continuer » — le curseur (ou le pouce) reste sur la même
                    cible. « Explication » se range dans la colonne de gauche,
                    collée à lui : elle ne pousse plus rien. */}
                <div className="mx-auto grid w-full max-w-5xl grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-3 py-3 sm:gap-3 sm:px-6 sm:py-3.5">
                    {/* Colonne gauche : « Explication » surgit en fondu au
                        verdict (façon Brilliant), à côté de l'action, jamais
                        à sa place. Copie Brilliant : bouton REMPLI gris clair,
                        même recette 3D que « Continuer » (lèvre border-b-4 qui
                        s'enfonce). La modale, elle, reste bleue. */}
                    <div className="flex items-center justify-end gap-2">
                        {hasExplain && (
                            <motion.button
                                type="button"
                                initial={reduce ? false : { opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                onClick={(e) => {
                                    if (isGhostClick(e)) return;
                                    setShowExplain(true);
                                }}
                                className="press-safe flex shrink-0 items-center gap-2 rounded-2xl border-b-4 border-b-gray-300 bg-gray-200 px-3.5 py-3 text-sm font-bold text-gray-700 transition-all duration-150 hover:bg-gray-300/70 active:translate-y-1 active:border-b-transparent dark:border-b-white/10 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/15 sm:px-5"
                            >
                                <SolarIcon name="lightbulb-bold" />
                                <span className="hidden sm:inline">{t("reps_explanation")}</span>
                                <kbd className="hidden rounded border border-gray-400/60 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-gray-600 opacity-90 dark:border-white/25 dark:text-grayDark-300 sm:inline">
                                    {modKey} I
                                </kbd>
                            </motion.button>
                        )}
                    </div>

                    {/* NAVIGATION LIBRE (mode revue) : Précédent / x sur y /
                        Suivant remplacent le bouton d'action — on feuillette,
                        on ne répond pas. Un interstitiel BeBot garde son
                        bouton Suivant, sinon il resterait à l'écran. */}
                    {reviewBrowsing && !interstitial ? (
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                onClick={reviewPrecedent}
                                disabled={reviewAuDebut && !lesson.prevLessonSlug}
                                title={reviewAuDebut ? "Leçon précédente" : undefined}
                                className="press-safe flex shrink-0 items-center gap-2 rounded-2xl border-b-4 border-b-gray-300 bg-gray-200 px-3.5 py-3 text-sm font-bold text-gray-700 transition-all duration-150 hover:bg-gray-300/70 active:translate-y-1 active:border-b-transparent disabled:cursor-not-allowed disabled:opacity-40 dark:border-b-white/10 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/15"
                            >
                                <SolarIcon name="arrow-left-linear" />
                                <span className="hidden sm:inline">
                                    {reviewAuDebut && lesson.prevLessonSlug
                                        ? "Leçon précédente"
                                        : "Précédent"}
                                </span>
                            </button>
                            <span className="min-w-[3.5rem] text-center text-xs font-semibold tabular-nums text-gray-400">
                                {cur + 1} / {steps.length}
                            </span>
                            <button
                                type="button"
                                onClick={reviewSuivant}
                                disabled={reviewAuBout && !lesson.nextLessonSlug}
                                title={reviewAuBout ? "Leçon suivante" : undefined}
                                className="press-safe flex shrink-0 items-center gap-2 rounded-2xl border-b-4 border-b-gray-300 bg-gray-200 px-3.5 py-3 text-sm font-bold text-gray-700 transition-all duration-150 hover:bg-gray-300/70 active:translate-y-1 active:border-b-transparent disabled:cursor-not-allowed disabled:opacity-40 dark:border-b-white/10 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/15"
                            >
                                <span className="hidden sm:inline">
                                    {reviewAuBout && lesson.nextLessonSlug
                                        ? "Leçon suivante"
                                        : "Suivant"}
                                </span>
                                <SolarIcon name="arrow-right-linear" />
                            </button>
                        </div>
                    ) : (
                    <button
                        ref={actionBtnRef}
                        type="button"
                        onClick={handleAction}
                        disabled={
                            interstitial ? false : !continueMode && !canCheck
                        }
                        className={`press-safe flex min-w-[8rem] shrink-0 items-center justify-center gap-2 rounded-2xl border-b-4 px-4 py-3 text-sm font-bold transition-all duration-150 active:translate-y-1 active:border-b-transparent sm:min-w-[10rem] sm:px-7 ${
                            interstitial
                                ? "border-b-emerald-700 bg-emerald-500 text-white hover:bg-emerald-600"
                                : btnCls
                        }`}
                    >
                        <motion.span
                            key={interstitial ? interLabel : actionLabel}
                            initial={reduce ? false : { opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.18, ease: "easeOut" }}
                        >
                            {interstitial ? interLabel : actionLabel}
                        </motion.span>
                        <kbd className="hidden rounded border border-white/40 px-1.5 py-0.5 text-[10px] font-semibold leading-none opacity-80 sm:inline">
                            ↵
                        </kbd>
                    </button>
                    )}

                    {/* Colonne droite : « Voir la solution » vit ICI, à droite
                        de « Continuer » — la gauche est le territoire de BeBot
                        (bulle + Explication), il n'a rien à y empiéter. Même
                        habit exactement qu'« Explication ». Vide le reste du
                        temps, la colonne équilibre la grille pour que l'action
                        reste parfaitement centrée. */}
                    <div className="flex items-center justify-start gap-2">
                        {proposerSolution && (
                            <motion.button
                                type="button"
                                initial={reduce ? false : { opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                onClick={(e) => {
                                    if (isGhostClick(e)) return;
                                    setShowSolution(true);
                                }}
                                className="press-safe flex shrink-0 items-center gap-2 rounded-2xl border-b-4 border-b-gray-300 bg-gray-200 px-3.5 py-3 text-sm font-bold text-gray-700 transition-all duration-150 hover:bg-gray-300/70 active:translate-y-1 active:border-b-transparent dark:border-b-white/10 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/15 sm:px-5"
                            >
                                <SolarIcon name="key-bold" />
                                <span className="hidden sm:inline">
                                    {t("reps_show_solution")}
                                </span>
                            </motion.button>
                        )}
                    </div>
                </div>
            </div>
            </div>
            </div>

            {/* BeBot PARLE (façon Brilliant) : bulle de verdict/commentaire en
                bas à gauche, posée au-dessus du BeBot persistant (desktop).
                Sur mobile (pas de dock), un mini BeBot accompagne la bulle. */}
            <AnimatePresence>
                {!chatOpen && bubble && (
                    <motion.div
                        key={bubble.key}
                        initial={
                            reduce
                                ? { opacity: 0 }
                                : { opacity: 0, y: 10, scale: 0.96 }
                        }
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.12 } }}
                        transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 26,
                            mass: 0.8,
                        }}
                        // z-[54] : la bulle est un feedback AMBIANT — au-dessus
                        // du contenu de l'étape (z ≤ 40) mais SOUS la barre
                        // d'action (z-[55], dont le popover d'upsell mobile) et
                        // sous le dock BeBot (z-[75], dont l'upsell desktop).
                        // La bulle « info » (le botSay d'un concept) a longtemps
                        // été masquée sous sm, parce que le dock BeBot auquel
                        // elle s'accroche n'existe qu'à partir de 640 px. Mais le
                        // mini BeBot ci-dessous existe précisément pour les
                        // petits écrans, et le mesuré était brutal : 453 bulles
                        // écrites et traduites qu'aucun téléphone n'affichait,
                        // ni ici ni dans l'app. Elle s'affiche désormais partout,
                        // comme les bulles de verdict et d'indice.
                        className="pointer-events-none fixed bottom-[86px] left-3 z-[54] flex items-end gap-2 sm:bottom-24 sm:left-5"
                    >
                        {/* Mini BeBot : le dock n'existe qu'à partir de sm. */}
                        <img
                            src={
                                bubble.kind === "ko"
                                    ? "/images/bebot/coach/sad.webp"
                                    : "/images/bebot/coach/happy.webp"
                            }
                            alt="BeBot"
                            className="h-10 w-10 shrink-0 sm:hidden object-contain"
                        />
                        <div
                            className={`relative max-w-[calc(100vw-84px)] rounded-2xl px-4 py-3 sm:max-w-xs ${
                                bubble.kind === "ok"
                                    ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-100"
                                    : bubble.kind === "ko"
                                      ? "bg-red-100 text-red-900 dark:bg-red-500/15 dark:text-red-100"
                                      : "bg-blue-100 text-gray-800 dark:bg-blue-500/15 dark:text-grayDark-100"
                            }`}
                        >
                            {/* Pointe vers BeBot (desktop) : c'est LUI qui parle.
                                Le dock est à left-5 avec un BeBot de 64px -> la
                                pointe (à 20px du bord de la bulle, elle-même à
                                left-5) tombe au-dessus de sa tête. Mobile : le
                                mini BeBot inline joue ce rôle. */}
                            <span
                                aria-hidden
                                className={`absolute left-5 top-full hidden h-0 w-0 border-x-[8px] border-t-[9px] border-x-transparent sm:block ${
                                    bubble.kind === "ok"
                                        ? "border-t-emerald-100 dark:border-t-emerald-500/20"
                                        : bubble.kind === "ko"
                                          ? "border-t-red-100 dark:border-t-red-500/15"
                                          : "border-t-blue-100 dark:border-t-blue-500/15"
                                }`}
                            />
                            {bubble.kind === "ok" ? (
                                <div className="text-[15px] font-semibold leading-snug">
                                    {t(
                                        CORRECT_CHEERS[
                                            flashId % CORRECT_CHEERS.length
                                        ]
                                    )}
                                </div>
                            ) : bubble.kind === "ko" ? (
                                <>
                                    <div className="text-[15px] font-semibold leading-snug">
                                        {t("reps_not_quite_title")}
                                    </div>
                                    <div className="mt-0.5 text-[13px] font-medium leading-snug text-red-700/80 dark:text-red-200/80">
                                        {t("reps_not_quite_subtitle")}
                                    </div>
                                    {/* Contestation (exercices de code, palier
                                        BeBot) : une vérification IA unique à la
                                        demande, dans la bulle. */}
                                    {(ex.type === "write_code" ||
                                        ex.type === "python_code" ||
                                        ex.type === "js_code") &&
                                        hasRepsAssistant(sessionData?.user) &&
                                        (appeals[cur]?.status === "loading" ? (
                                            <div className="mt-1 text-xs text-red-700/70 dark:text-red-200/70">
                                                {t("reps_appeal_pending")}
                                            </div>
                                        ) : appeals[cur]?.status === "denied" ? (
                                            <div className="mt-1 text-xs text-red-700/70 dark:text-red-200/70">
                                                {appeals[cur].reason ||
                                                    t("reps_appeal_denied")}
                                            </div>
                                        ) : !appeals[cur] ||
                                          appeals[cur].status === "error" ? (
                                            // status "error" : requestAppeal
                                            // autorise le réessai, le bouton
                                            // doit donc revenir.
                                            <button
                                                type="button"
                                                onClick={requestAppeal}
                                                className="pointer-events-auto mt-1 text-xs font-semibold text-red-800 underline underline-offset-2 hover:text-red-900 dark:text-red-200 dark:hover:text-red-100"
                                            >
                                                {t("reps_appeal_button")}
                                            </button>
                                        ) : null)}
                                </>
                            ) : bubble.kind === "hint" ? (
                                <div className="text-[15px] leading-snug">
                                    {renderMarkdownInline(stepHint)}
                                </div>
                            ) : (
                                <div className="text-[15px] leading-snug">
                                    {/* Même rendu que la bulle d'indice juste
                                        au-dessus : un `mot` en backticks est du
                                        code, pas des backticks à lire. BeBot
                                        cite des propriétés et des valeurs, il
                                        est le dernier endroit du player où on
                                        pouvait se permettre du texte brut. */}
                                    {renderMarkdownInline(ex.data.botSay)}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* DESKTOP : BeBot PERSISTANT (Lottie), monte en permanence, jamais
                demonte -> il reste pose en bas a gauche sans disparaitre ; clic =
                ouvrir/fermer. Masque en douceur (opacite) pendant fin/interstitiel
                sans le demonter (la Lottie ne repart pas de zero). C'est LUI qui
                porte le verdict : pose triste sur une mauvaise réponse. */}
            <RepsAssistantDock
                open={chatOpen}
                onToggle={() => setChatOpen((v) => !v)}
                hidden={done || interstitial}
                mood={locked && !lastCorrect ? "sad" : "happy"}
            />


            {/* Modale d'explication (couleur bleue = apprentissage) */}
            <AnimatePresence>
                {showSolution && proposerSolution && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setShowSolution(false)}
                        className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/40 p-0 backdrop-blur-[6px] sm:items-center sm:p-6"
                    >
                        <motion.div
                            initial={{ y: "100%", opacity: 0.6 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: "100%", opacity: 0 }}
                            transition={{ type: "spring", damping: 32, stiffness: 380, mass: 0.75 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl dark:bg-bgDark-840 sm:mx-4 sm:max-w-lg sm:rounded-3xl"
                        >
                            <div className="flex justify-center pb-0 pt-3 sm:hidden">
                                <div className="h-[3px] w-9 rounded-full bg-gray-200 dark:bg-white/15" />
                            </div>
                            <div className="flex items-center gap-3 px-6 pb-2 pt-5 sm:pt-6">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">
                                    <SolarIcon name="key-bold" className="text-lg" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-[17px] font-bold tracking-tight text-gray-900 dark:text-white">
                                        {t("reps_solution_title")}
                                    </h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowSolution(false)}
                                    className="-mr-1.5 -mt-0.5 rounded-xl p-2 text-gray-300 transition-colors hover:bg-gray-50 hover:text-gray-500 dark:text-grayDark-500 dark:hover:bg-white/[0.06]"
                                >
                                    <SolarIcon name="close-circle-linear" className="text-xl" />
                                </button>
                            </div>
                            <div className="max-h-[55vh] overflow-y-auto px-6 pb-5">
                                {reponseMontrable.kind === "code" ? (
                                    <div className="overflow-hidden rounded-2xl bg-[#1e1e1e]">
                                        <div className="overflow-x-auto px-4 py-3.5">
                                            {tokenizeLines(
                                                reponseMontrable.value,
                                                reponseMontrable.lang || "html"
                                            ).map((ligne, i) => (
                                                <div
                                                    key={i}
                                                    className="whitespace-pre font-geistMono text-[13px] leading-[1.8] text-[#d4d4d4]"
                                                >
                                                    {ligne.length
                                                        ? ligne.map((tk, j) =>
                                                              tk.kind ? (
                                                                  <span key={j} style={{ color: TOKEN_DARK[tk.kind] }}>
                                                                      {tk.text}
                                                                  </span>
                                                              ) : (
                                                                  <span key={j}>{tk.text}</span>
                                                              )
                                                          )
                                                        : " "}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-[15px] leading-[1.7] text-gray-700 dark:text-grayDark-200">
                                        {renderMarkdownInline(reponseMontrable.value)}
                                    </div>
                                )}
                            </div>
                            <div
                                className="border-t border-gray-100 px-6 pt-3 dark:border-white/[0.04]"
                                style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setShowSolution(false)}
                                    className="press-safe w-full rounded-2xl border-b-4 border-gray-200 bg-gray-100 py-3 text-sm font-bold text-gray-700 transition-all hover:bg-gray-200 active:translate-y-[2px] active:border-b-2 dark:border-black/40 dark:bg-white/[0.08] dark:text-grayDark-100 dark:hover:bg-white/[0.12] [--press-drop:2px]"
                                >
                                    {t("reps_understood")}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
                {showExplain && hasExplain && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setShowExplain(false)}
                        className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/40 p-0 backdrop-blur-[6px] sm:items-center sm:p-6"
                    >
                        <motion.div
                            initial={{ y: "100%", opacity: 0.6 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: "100%", opacity: 0 }}
                            transition={{
                                type: "spring",
                                damping: 32,
                                stiffness: 380,
                                mass: 0.75,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl dark:bg-bgDark-840 sm:mx-4 sm:max-w-lg sm:rounded-3xl"
                        >
                            <div className="flex justify-center pb-0 pt-3 sm:hidden">
                                <div className="h-[3px] w-9 rounded-full bg-gray-200 dark:bg-white/15" />
                            </div>
                            <div className="flex items-center gap-3 px-6 pb-2 pt-5 sm:pt-6">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                                    <SolarIcon name="lightbulb-bold" className="text-lg" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-[17px] font-bold tracking-tight text-gray-900 dark:text-white">
                                        {t("reps_why_title")}
                                    </h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowExplain(false)}
                                    className="-mr-1.5 -mt-0.5 rounded-xl p-2 text-gray-300 transition-colors hover:bg-gray-50 hover:text-gray-500 dark:text-grayDark-500 dark:hover:bg-white/[0.06]"
                                >
                                    <SolarIcon name="close-circle-linear" className="text-xl" />
                                </button>
                            </div>
                            <div className="max-h-[50vh] overflow-y-auto px-6 pb-5 text-[14px] leading-[1.7] text-gray-600 dark:text-grayDark-300">
                                {renderMarkdownInline(ex.explanation)}
                            </div>
                            <div
                                className="border-t border-gray-100 px-6 pt-3 dark:border-white/[0.04]"
                                style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setShowExplain(false)}
                                    className="w-full rounded-2xl bg-blue-500 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-500/20 transition-all hover:bg-blue-600 active:scale-[0.97]"
                                >
                                    {t("reps_understood")}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modale de confirmation de sortie */}
            <AnimatePresence>
                {showQuit && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setShowQuit(false)}
                        className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/40 p-0 backdrop-blur-[6px] sm:items-center sm:p-6"
                    >
                        <motion.div
                            initial={{ y: "100%", opacity: 0.6 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: "100%", opacity: 0 }}
                            transition={{
                                type: "spring",
                                damping: 32,
                                stiffness: 380,
                                mass: 0.75,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl dark:bg-bgDark-840 sm:mx-4 sm:max-w-md sm:rounded-3xl"
                        >
                            <div className="flex justify-center pb-0 pt-3 sm:hidden">
                                <div className="h-[3px] w-9 rounded-full bg-gray-200 dark:bg-white/15" />
                            </div>
                            <div className="px-6 pb-2 pt-6 text-center">
                                <h3 className="text-[18px] font-bold tracking-tight text-gray-900 dark:text-white">
                                    {t("reps_quit_title")}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                                    {t("reps_quit_subtitle")}
                                </p>
                            </div>
                            <div
                                className="flex flex-col gap-2 px-6 pt-4 sm:flex-row"
                                style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
                            >
                                <button
                                    type="button"
                                    onClick={() => setShowQuit(false)}
                                    className="order-2 flex-1 rounded-2xl border border-gray-200 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/15 dark:text-grayDark-300 dark:hover:bg-white/[0.05] sm:order-1"
                                >
                                    {t("reps_quit_continue")}
                                </button>
                                <Link
                                    href={backHref}
                                    className="order-1 flex-1 rounded-2xl bg-red-500 py-3 text-center text-sm font-semibold !text-white transition-colors hover:bg-red-600 sm:order-2"
                                >
                                    {t("reps_quit_confirm")}
                                </Link>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modale de signalement (composant partagé avec les quiz) */}
            <ReportModal
                open={showReport}
                onClose={() => setShowReport(false)}
                reasons={reportReasons}
                prompt={t("reps_report_prompt")}
                placeholder={t("reps_report_placeholder")}
                onSubmit={submitReport}
            />

            {/* MODE REVUE : motifs d'invalidation de l'étape courante. Les
                identifiants de motifs sont ceux du registre de jurisprudence
                (docs/reps/review/REGISTRY.md) : le verdict se digère en entrée
                du registre sans traduction. Français en dur : admin only. */}
            <BottomSheetModal
                isOpen={showReviewKo}
                onClose={() => setShowReviewKo(false)}
                portal
                zIndexClass="z-[10000]"
            >
                <div className="p-5">
                    <h3 className="mb-1 text-base font-bold text-gray-900 dark:text-white">
                        Invalider cette étape
                    </h3>
                    <p className="mb-4 text-sm text-gray-500 dark:text-grayDark-400">
                        Le verdict ouvre un ticket avec le step figé dedans.
                    </p>
                    <div className="mb-4 flex flex-wrap gap-2">
                        {[
                            ["vocabulaire", "Vocabulaire"],
                            ["trop-difficile", "Trop difficile"],
                            ["ambigu", "Ambigu"],
                            ["reponse-fausse", "Réponse fausse"],
                            ["mauvais-type", "Mauvais type d'exercice"],
                            ["decor-inconnu", "Décor inconnu"],
                            ["consigne", "Consigne"],
                            ["autre", "Autre"],
                        ].map(([id, label]) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setReviewMotif(id)}
                                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                                    reviewMotif === id
                                        ? "border-rose-500 bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400"
                                        : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-grayDark-700 dark:text-grayDark-300 dark:hover:border-grayDark-600"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        rows={3}
                        placeholder="Précision (optionnelle) : ce qui cloche, la reformulation attendue…"
                        className="mb-4 w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-hidden focus:border-gray-400 dark:border-grayDark-700 dark:bg-grayDark-900 dark:text-white dark:focus:border-grayDark-500"
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setShowReviewKo(false)}
                            className="rounded-full px-4 py-2 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-700 dark:text-grayDark-400 dark:hover:text-white"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            disabled={!reviewMotif || reviewSending}
                            onClick={async () => {
                                setShowReviewKo(false);
                                await sendReviewVerdict(
                                    "ko",
                                    reviewMotif,
                                    reviewNote.trim() || null
                                );
                            }}
                            className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Invalider l'étape
                        </button>
                    </div>
                </div>
            </BottomSheetModal>

        </div>
    );
}

// Écrans sombres du défi légendaire (intro / échec) : rupture voulue avec le
// reste du site (fond #0E1020 en clair COMME en sombre, pattern Duolingo),
// hero centré, CTA doré à facettes en bas + lien texte de sortie.
function LegendaryGate({ hero, title, subtitle, cta, onCta, link, linkHref }) {
    return (
        <div className="fixed inset-0 z-[60] flex flex-col bg-[#0E1020] text-white">
            <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
                {hero}
                {title ? (
                    <h2 className="mt-6 text-2xl font-extrabold">{title}</h2>
                ) : null}
                <p className="mt-3 max-w-md text-[17px] leading-6 text-gray-400">
                    {subtitle}
                </p>
            </div>
            <div className="mx-auto w-full max-w-md px-5 pb-8">
                <button
                    type="button"
                    onClick={onCta}
                    className="press-safe block w-full rounded-2xl border-b-4 border-b-[#DFAE35] py-3.5 text-center text-base font-extrabold uppercase text-[#6A3D00] active:translate-y-1 active:border-b-transparent"
                    style={{ background: LEGENDARY_FACETS }}
                >
                    {cta}
                </button>
                <Link
                    href={linkHref}
                    className="mt-4 block py-2 text-center text-base font-bold text-gray-400 transition-colors hover:text-white"
                >
                    {link}
                </Link>
            </div>
        </div>
    );
}
